/* ==========================================================================
   SHOP-110 — Reformer Build Flow
   --------------------------------------------------------------------------
   Three custom elements:

     <reformer-flow>          the controller + state machine
     <reformer-flow-panel>    an accessible panel (modal drawer/sheet, or an
                              in-page card) with focus management
     <reformer-render-stage>  layered-PNG compositor that lives in the gallery
                              slot and answers back to each choice

   The flow never navigates. Postcode is collected first because warehouse,
   lead time and stock all derive from it; after that the editorial gallery is
   replaced in place by the render and the card drives it.
   ========================================================================== */

(function () {
  'use strict';

  if (window.ReformerFlowLoaded) return;
  window.ReformerFlowLoaded = true;

  /* ----------------------------------------------------------------------
     Small helpers
     ---------------------------------------------------------------------- */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), summary, ' +
    '[tabindex]:not([tabindex^="-"])';

  function focusable(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      if (el.closest('[hidden]')) return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      // offsetParent is null for display:none; position:fixed needs the rect check.
      return el.offsetParent !== null || el.getClientRects().length > 0;
    });
  }

  /** matchMedia is the only platform API the flow leans on for layout
   *  decisions. Guard it rather than let one missing feature take the whole
   *  purchase path down. */
  function media(query) {
    if (typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia(query).matches;
    } catch (e) {
      return false;
    }
  }

  function prefersReducedMotion() {
    return media('(prefers-reduced-motion: reduce)');
  }

  function isDesktop() {
    return media('(min-width: 990px)');
  }

  /** Shopify money formatting, kept local so the flow has no load-order
   *  dependency on vendor scripts. Mirrors Shopify.formatMoney. */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function defaultTo(num, precision, thousands, decimal) {
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(num) || num === null) return 0;
      num = (num / 100.0).toFixed(precision);
      var parts = num.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    switch ((formatString.match(placeholderRegex) || [])[1]) {
      case 'amount':
        value = defaultTo(cents, 2);
        break;
      case 'amount_no_decimals':
        value = defaultTo(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = defaultTo(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = defaultTo(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = defaultTo(cents, 0, ' ');
        break;
      case 'amount_with_space_separator':
        value = defaultTo(cents, 2, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = defaultTo(cents, 2, "'");
        break;
      default:
        value = defaultTo(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ----------------------------------------------------------------------
     Background inertness
     ----------------------------------------------------------------------
     While a modal panel is open everything else on the page is made inert so
     screen readers and the Tab key cannot reach it. `inert` is supported
     everywhere we ship to; aria-hidden is kept as the belt-and-braces path.
     ---------------------------------------------------------------------- */

  var inertStack = [];

  function setBackgroundInert(keepEl) {
    var siblings = [];
    var node = keepEl;
    while (node && node !== document.body) {
      var parent = node.parentElement;
      if (!parent) break;
      Array.prototype.forEach.call(parent.children, function (child) {
        if (child === node) return;
        if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE') return;
        if (child.hasAttribute('inert')) return;
        // The flow's live region has to stay in the accessibility tree, or
        // step changes stop being announced the moment a dialog opens.
        if (child.hasAttribute('data-rf-announcer')) return;
        child.setAttribute('inert', '');
        child.setAttribute('aria-hidden', 'true');
        siblings.push(child);
      });
      node = parent;
    }
    inertStack.push(siblings);
  }

  function releaseBackgroundInert() {
    var siblings = inertStack.pop();
    if (!siblings) return;
    siblings.forEach(function (el) {
      // The build seals the page below it for as long as it is open, and that
      // outlives the postcode dialog that opened over the top. Closing the
      // dialog must not hand those sections back.
      if (el.classList && el.classList.contains('rf-flow-sealed')) return;
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    });
  }

  /* ----------------------------------------------------------------------
     Scroll lock that keeps the page exactly where it was
     ---------------------------------------------------------------------- */

  var scrollLock = { count: 0, y: 0 };

  function lockScroll() {
    if (scrollLock.count++ > 0) return;
    scrollLock.y = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollLock.y + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.classList.add('rf-scroll-locked');
  }

  function unlockScroll() {
    if (scrollLock.count === 0) return;
    if (--scrollLock.count > 0) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.classList.remove('rf-scroll-locked');
    // base.css sets `html { scroll-behavior: smooth }`, which turned this
    // restore into a visible glide down from the top of the page, since
    // unpinning the body has just put it there. It has to land in one frame.
    var html = document.documentElement;
    var behavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, scrollLock.y);
    html.style.scrollBehavior = behavior;
  }

  /* ======================================================================
     <reformer-render-stage>
     ----------------------------------------------------------------------
     Layered PNG plates on one locked camera. Each layer owns two <img>
     buffers so a colour change is a crossfade rather than a flash. If a plate
     has not loaded we hold the current one — never a spinner, never a
     half-drawn Reformer.
     ====================================================================== */

  var LAYERS = ['base', 'upholstery', 'accessories', 'props'];

  class ReformerRenderStage extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.scene = document.createElement('div');
      this.scene.className = 'rf-stage__scene';
      // The render is decorative: every colour it shows is also stated in
      // words in the selected row of the card, so colour is never carried by
      // the image alone.
      this.scene.setAttribute('role', 'presentation');
      this.appendChild(this.scene);

      this.caption = document.createElement('p');
      this.caption.className = 'rf-stage__caption';
      this.caption.hidden = true;
      this.appendChild(this.caption);

      this.buffers = {};
      LAYERS.forEach(
        function (layer) {
          this.buffers[layer] = [this.makePlate(layer), this.makePlate(layer)];
          this.buffers[layer].active = 0;
        }.bind(this)
      );

      this.cache = {};
    }

    makePlate(layer) {
      var img = document.createElement('img');
      img.className = 'rf-stage__plate rf-stage__plate--' + layer;
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.decoding = 'async';
      this.scene.appendChild(img);
      return img;
    }

    /** Warm the browser cache for every plate we might show, so step 04 never
     *  shows a spinner or a half-drawn Reformer. */
    preload(urls) {
      (urls || []).forEach(
        function (url) {
          if (!url || this.cache[url]) return;
          var img = new Image();
          img.decoding = 'async';
          img.src = url;
          this.cache[url] = img;
        }.bind(this)
      );
    }

    /**
     * @param {string} layer
     * @param {string|null} url
     * @param {{placement?: {left?:string,bottom?:string,width?:string}, cutOut?: boolean}} [opts]
     *   placement positions a layer within the scene instead of filling it —
     *   the jumpboard and the prop kit are objects sitting on the ground, not
     *   full-frame plates. cutOut:false means the source is still product
     *   photography on a light background, so it is multiplied onto the ground
     *   rather than pasted as a rectangle.
     */
    setLayer(layer, url, opts) {
      var pair = this.buffers && this.buffers[layer];
      if (!pair) return;

      opts = opts || {};
      pair.forEach(function (img) {
        var p = opts.placement;
        if (p) {
          img.style.inset = 'auto';
          img.style.left = p.left || '0';
          img.style.top = p.top || 'auto';
          img.style.bottom = p.top ? 'auto' : p.bottom || '0';
          img.style.width = p.width || '100%';
          img.style.height = p.height || 'auto';
        } else {
          img.style.inset = '';
          img.style.left = '';
          img.style.top = '';
          img.style.bottom = '';
          img.style.width = '';
          img.style.height = '';
        }
        img.classList.toggle('rf-stage__plate--knockout', opts.cutOut === false);
      });

      // What this layer is meant to be showing, independent of whether the
      // image has finished loading. Readable in devtools and assertable in
      // tests, where images never fire load.
      this.setAttribute('data-layer-' + layer, url || '');

      if (!url) {
        pair[0].classList.remove('is-shown');
        pair[1].classList.remove('is-shown');
        return;
      }

      var current = pair[pair.active];
      if (current.getAttribute('src') === url && current.classList.contains('is-shown')) return;

      var next = pair[1 - pair.active];
      var show = function () {
        next.classList.add('is-shown');
        current.classList.remove('is-shown');
        pair.active = 1 - pair.active;
      };

      if (next.getAttribute('src') === url && next.complete) {
        show();
        return;
      }

      next.onload = show;
      // A plate that fails to load leaves the previous one in place rather
      // than blanking the scene.
      next.onerror = function () {
        next.onload = null;
      };
      next.src = url;
      if (next.complete && next.naturalWidth) show();
    }

    setCaption(text) {
      if (!this.caption) return;
      this.caption.textContent = text || '';
      this.caption.hidden = !text;
    }

    show() {
      this.classList.add('is-active');
    }

    hide() {
      this.classList.remove('is-active');
    }
  }

  if (!customElements.get('reformer-render-stage')) {
    customElements.define('reformer-render-stage', ReformerRenderStage);
  }

  /* ======================================================================
     <reformer-flow-panel>
     ----------------------------------------------------------------------
     One shell, two presentations.

       mode="modal"  — focus-trapped dialog. Bottom sheet on mobile (rises
                       with the keyboard, drag to dismiss), right-hand drawer
                       on desktop. Scrim, Esc, tap-outside, focus return.
       mode="inline" — an in-page card. Not a dialog: the page stays usable,
                       focus moves to the step heading on change and step
                       changes are announced through the flow's live region.
     ====================================================================== */

  class ReformerFlowPanel extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.isOpen = false;
      this.openedBy = null;

      this.onKeydown = this.handleKeydown.bind(this);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onViewport = this.handleViewport.bind(this);

      this.panel = this.querySelector('.rf-panel') || this;
      this.scrim = this.querySelector('[data-rf-scrim]');
      this.body = this.querySelector('.rf-panel__body');

      if (this.scrim) {
        this.scrim.addEventListener('click', this.requestClose.bind(this, 'scrim'));
      }

      this.querySelectorAll('[data-rf-dismiss]').forEach(
        function (el) {
          el.addEventListener('click', this.requestClose.bind(this, 'button'));
        }.bind(this)
      );

      this.setupDrag();
      this.setupScrollShadow();

      // A modal panel is rendered inside the buy box, which is sticky and so
      // forms its own stacking context: however high its z-index, it could
      // never sit above the header while the buy box sits below it. The top
      // layer is above every stacking context, without moving the panel out
      // of the DOM the controller and the stylesheet expect it in. Scrim first,
      // so the panel is shown above it.
      this.topLayer = this.modal && typeof this.panel.showPopover === 'function';
      if (this.topLayer) {
        if (this.scrim) this.scrim.setAttribute('popover', 'manual');
        this.panel.setAttribute('popover', 'manual');
      }
    }

    setTopLayer(on) {
      if (!this.topLayer) return;
      [this.scrim, this.panel].forEach(function (el) {
        if (!el) return;
        var open = el.matches(':popover-open');
        if (on && !open) el.showPopover();
        if (!on && open) el.hidePopover();
      });
    }

    get modal() {
      return this.getAttribute('mode') !== 'inline';
    }

    /* ---- open / close ---- */

    open(openedBy) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.openedBy = openedBy || document.activeElement;

      // A close still mid-transition must not hide the panel we are reopening.
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      this.panel.hidden = false;

      if (this.modal) {
        if (this.scrim) this.scrim.hidden = false;
        this.setTopLayer(true);
        if (this.scrim) {
          // force a frame so the opacity transition actually runs
          void this.scrim.offsetWidth;
          this.scrim.classList.add('is-visible');
        }
        lockScroll();
        setBackgroundInert(this);
        document.addEventListener('keydown', this.onKeydown, true);
        document.addEventListener('focusin', this.onFocusIn, true);
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this.onViewport);
          window.visualViewport.addEventListener('scroll', this.onViewport);
        }
      }

      void this.panel.offsetWidth;
      this.panel.classList.add('is-open');

      this.dispatchEvent(new CustomEvent('rf:panel-open', { bubbles: true }));
      this.focusFirst();
    }

    close(reason) {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.panel.classList.remove('is-open');

      if (this.modal) {
        document.removeEventListener('keydown', this.onKeydown, true);
        document.removeEventListener('focusin', this.onFocusIn, true);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this.onViewport);
          window.visualViewport.removeEventListener('scroll', this.onViewport);
        }
        releaseBackgroundInert();
        unlockScroll();
        if (this.scrim) this.scrim.classList.remove('is-visible');
      }

      var finish = function () {
        this.closeTimer = null;
        if (this.isOpen) return;
        this.panel.hidden = true;
        if (this.scrim) this.scrim.hidden = true;
        this.setTopLayer(false);
      }.bind(this);

      if (prefersReducedMotion()) {
        finish();
      } else {
        this.closeTimer = window.setTimeout(finish, 240);
      }

      // Listeners get to restore the page (un-hide the buy box, drop the
      // render) before focus goes home, so focus never lands on a hidden node.
      this.dispatchEvent(
        new CustomEvent('rf:panel-close', { bubbles: true, detail: { reason: reason } })
      );

      if (this.openedBy && document.contains(this.openedBy) && this.openedBy.offsetParent !== null) {
        try {
          this.openedBy.focus({ preventScroll: true });
        } catch (e) {
          this.openedBy.focus();
        }
      }
    }

    requestClose(reason, event) {
      if (event) event.preventDefault();
      if (this.getAttribute('data-dismissible') === 'false') {
        this.dispatchEvent(new CustomEvent('rf:panel-dismiss-blocked', { bubbles: true }));
        return;
      }
      this.close(reason);
    }

    /* ---- focus ---- */

    focusFirst() {
      // `:not([hidden])` only looks at the element itself. The panel holds one
      // autofocus target per view, so the one in a hidden view has to be ruled
      // out by its ancestors or focus lands on a display:none node and falls
      // back to <body>.
      var target =
        Array.prototype.filter.call(this.querySelectorAll('[data-rf-autofocus]'), function (el) {
          return !el.closest('[hidden]');
        })[0] ||
        this.querySelector('.rf-step:not([hidden]) [data-rf-step-heading]') ||
        focusable(this)[0] ||
        this.panel;

      if (target && target.hasAttribute && !target.hasAttribute('tabindex') && !focusable(this).includes(target)) {
        target.setAttribute('tabindex', '-1');
      }

      window.requestAnimationFrame(
        function () {
          // Closed again before the frame landed — do not steal focus back.
          if (!this.isOpen) return;
          try {
            target.focus({ preventScroll: true });
          } catch (e) {
            if (target.focus) target.focus();
          }
        }.bind(this)
      );
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape' || event.key === 'Esc') {
        event.stopPropagation();
        this.requestClose('escape');
        return;
      }

      if (event.key !== 'Tab') return;

      var items = focusable(this);
      if (!items.length) {
        event.preventDefault();
        return;
      }
      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /** Anything that escapes the trap (browser chrome, extensions) gets pulled
     *  back to the panel. */
    handleFocusIn(event) {
      if (!this.isOpen || this.contains(event.target)) return;
      var items = focusable(this);
      (items[0] || this.panel).focus();
    }

    /* ---- keyboard-safe bottom sheet ----
       The sheet rises with the on-screen keyboard so the primary action is
       never covered. This is the one place a mobile flow like this usually
       breaks. */

    handleViewport() {
      if (!window.visualViewport || isDesktop()) {
        this.panel.style.setProperty('--rf-keyboard-inset', '0px');
        return;
      }
      var inset = Math.max(
        0,
        window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop
      );
      this.panel.style.setProperty('--rf-keyboard-inset', inset + 'px');
    }

    /* ---- drag to dismiss ---- */

    setupDrag() {
      var grabber = this.querySelector('.rf-panel__grabber');
      if (!grabber) return;

      var startY = 0;
      var delta = 0;
      var dragging = false;

      var onMove = function (e) {
        if (!dragging) return;
        delta = Math.max(0, e.touches[0].clientY - startY);
        this.panel.style.transform = 'translateY(' + delta + 'px)';
      }.bind(this);

      var onEnd = function () {
        if (!dragging) return;
        dragging = false;
        this.panel.classList.remove('is-dragging');
        this.panel.style.transform = '';
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onEnd);
        if (delta > 90) this.requestClose('drag');
      }.bind(this);

      grabber.addEventListener(
        'touchstart',
        function (e) {
          if (isDesktop() || prefersReducedMotion()) return;
          dragging = true;
          delta = 0;
          startY = e.touches[0].clientY;
          this.panel.classList.add('is-dragging');
          document.addEventListener('touchmove', onMove, { passive: true });
          document.addEventListener('touchend', onEnd);
        }.bind(this),
        { passive: true }
      );
    }

    /* ---- footer hairline appears only once the body actually scrolls ---- */

    setupScrollShadow() {
      if (!this.body) return;
      var update = function () {
        var scrollable = this.body.scrollHeight - this.body.clientHeight > 2;
        var atBottom = this.body.scrollTop + this.body.clientHeight >= this.body.scrollHeight - 2;
        this.panel.classList.toggle('is-scrolling', scrollable && !atBottom);
      }.bind(this);

      this.body.addEventListener('scroll', update, { passive: true });
      this.updateScrollShadow = update;

      if ('ResizeObserver' in window) {
        new ResizeObserver(update).observe(this.body);
      }
    }
  }

  if (!customElements.get('reformer-flow-panel')) {
    customElements.define('reformer-flow-panel', ReformerFlowPanel);
  }

  /* ======================================================================
     <reformer-flow>
     ====================================================================== */

  class ReformerFlow extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      var dataEl = this.querySelector('[data-rf-config]');
      try {
        this.config = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[reformer-flow] could not parse config', e);
        return;
      }

      // Delivery-zone copy is authored in the theme editor, so it arrives as a
      // string and is parsed separately: a typo must not take the flow down.
      this.config.zones = this.parseSetting(this.config.zonesRaw, {});
      this.config.placement = this.parseSetting(this.config.placementRaw, {});

      this.storageKey = this.getAttribute('data-storage-key') || 'yr:reformer-flow';
      this.postcodeKey = 'yr:delivery-postcode';
      this.sectionEl = this.closest('[id^="shopify-section-"]') || document.body;
      this.announcer = this.querySelector('[data-rf-announcer]');
      this.postcodePanel = this.querySelector('[data-rf-panel="postcode"]');
      this.buildPanel = this.querySelector('[data-rf-panel="build"]');
      this.stage = document.querySelector(this.getAttribute('data-stage-target') || '#ReformerStage');
      this.stageHost = this.stage ? this.stage.parentElement : null;
      this.thumbTrack = document.querySelector('.pdp-thumb-track');

      this.state = this.restore();
      this.explicitExtraPicks = {};
      this.steps = this.buildStepList();

      this.cacheNodes();
      this.bind();

      // A link is read before hydration so the controls are drawn already
      // holding what it asked for, rather than flickering through the
      // session's build on the way.
      this.applyUrlState(this.readUrlState());

      this.hydrateFromState();

      this.removeAttribute('hidden');

      this.openFromUrl();
    }

    parseSetting(raw, fallback) {
      if (!raw) return fallback;
      try {
        return JSON.parse(raw);
      } catch (e) {
        console.warn('[reformer-flow] ignoring malformed setting JSON', e);
        return fallback;
      }
    }

    /* ---- step list ------------------------------------------------------
       Built from what actually exists, so if the prop kit turns out to be a
       single SKU the flow becomes 3 of 3 and every footer follows. */

    buildStepList() {
      var steps = [];
      // Frame first, then that frame's colourways. With one colour per frame
      // the frame is the whole choice. Matches the Liquid's rf_uph_min.
      var frames = this.frames();
      if (frames.length > 1) steps.push('frame');
      if (this.config.upholstery && this.config.upholstery.length > Math.max(frames.length, 1)) {
        steps.push('upholstery');
      }
      if (this.config.propKit && this.config.propKit.options && this.config.propKit.options.length > 1) {
        steps.push('props');
      }
      // Inclusion products that asked for a step (has_build_step) and have a
      // real choice to make, in Products-list order after the prop kit.
      this.steppedInclusionProducts().forEach(function (entry) {
        steps.push('inc-' + entry.id);
      });
      if (this.config.extras && this.config.extras.length) steps.push('extras');
      // Payment comes last, once the whole build is chosen (SHOP-145).
      if (this.config.payment && this.config.payment.length > 1) steps.push('payment');
      steps.push('summary');
      return steps;
    }

    get buildStepCount() {
      return this.steps.length - 1; // summary is not numbered
    }

    cacheNodes() {
      this.trigger = document.querySelector('[data-rf-trigger]');
      this.resumeBtn = document.querySelector('[data-rf-resume]');
      this.quickAddBtn = document.querySelector('[data-rf-quick-add]');

      // The buy box price, which the flow owns while it is taking the PDP over.
      // Outside the element, like the trigger.
      this.priceHost = document.querySelector('.rf-price-host');
      this.priceSupplement = document.querySelector('.plan-price-supplement');
      this.priceWas = document.querySelector('[data-rf-price-was]');
      this.priceDeposit = document.querySelector('[data-rf-price-deposit]');
      this.pricePer = document.querySelector('[data-rf-price-per]');
      this.priceTerm = document.querySelector('[data-rf-price-term]');
      // The PDP's payment pills, when the section renders them (SHOP-154).
      var section = this.closest('[id^="shopify-section-"]');
      this.planPills = section ? section.querySelector('.plan-pill-block') : null;
      this.postcodeInput = this.querySelector('[data-rf-postcode-input]');
      this.postcodeResolved = this.querySelector('[data-rf-postcode-resolved]');
      this.postcodeError = this.querySelector('[data-rf-postcode-error]');
      this.postcodeConfirm = this.querySelector('[data-rf-postcode-confirm]');
      this.postcodeDetected = this.querySelector('[data-rf-postcode-detected]');
      this.postcodeViews = {
        ask: this.querySelector('[data-rf-postcode-view="ask"]'),
        served: this.querySelector('[data-rf-postcode-view="served"]'),
        unserved: this.querySelector('[data-rf-postcode-view="unserved"]')
      };
      this.stepNodes = {};
      this.querySelectorAll('[data-rf-step]').forEach(
        function (el) {
          this.stepNodes[el.getAttribute('data-rf-step')] = el;
        }.bind(this)
      );
      this.progressSegments = this.querySelectorAll('[data-rf-progress-segment]');
      this.progressTrack = this.querySelector('[data-rf-progress-track]');
      this.progressCount = this.querySelector('[data-rf-progress-count]');
      this.summaryTotals = this.querySelector('[data-rf-summary-totals]');
      this.summaryAmount = this.querySelector('[data-rf-summary-amount]');
      this.planPrice = this.querySelector('[data-rf-plan-price]');
      this.planWas = this.querySelector('[data-rf-plan-was]');
      this.planDeposit = this.querySelector('[data-rf-plan-deposit]');
      this.planPer = this.querySelector('[data-rf-plan-per]');
      this.planTerm = this.querySelector('[data-rf-plan-term]');
      this.footTotal = this.querySelector('[data-rf-total]');
      this.footMeta = this.querySelector('[data-rf-total-meta]');
      this.nextBtn = this.querySelector('[data-rf-next]');
      this.backBtn = this.querySelector('[data-rf-back]');

      // The extras step is two views: the list, and the colourway picker it
      // slides across to. Which one is up is held here, not in this.state —
      // it is a view, not a decision, and must not survive a reload.
      this.shareBtn = this.querySelector('[data-rf-share]');
      this.stockNotice = this.querySelector('[data-rf-stock-notice]');
      this.extrasList = this.querySelector('[data-rf-extras-view="list"]');
      this.extrasPickerView = this.querySelector('[data-rf-extras-view="picker"]');
      this.extrasPickerProduct = null;
      this.addBtn = this.querySelector('[data-rf-add]');
      this.summaryList = this.querySelector('[data-rf-summary]');
      this.cartError = this.querySelector('[data-rf-cart-error]');
    }

    /* ---- wiring ---- */

    bind() {
      var self = this;

      if (this.trigger) {
        this.trigger.addEventListener('click', function (e) {
          e.preventDefault();
          self.start(this);
        });
      }

      if (this.resumeBtn) {
        this.resumeBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.quickPending = false;
          self.openPostcode(this);
        });
      }

      if (this.quickAddBtn) {
        this.quickAddBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.quickStart(this);
        });
      }

      // The two breakpoints compose the scene differently, so crossing between
      // them has to recompose it. Only on an actual crossing: resize fires
      // constantly on a phone as the address bar hides and shows.
      this.onBreakpoint = function () {
        var key = isDesktop() ? 'desktop' : 'mobile';
        if (key === self.sceneBreakpoint) return;
        self.renderScene();
      };
      window.addEventListener('resize', this.onBreakpoint);

      // A tap on a colour is the choice: straight back to the list.
      // Pointer only (detail > 0): arrow keys pick as they move, so keyboard
      // users keep Confirm.
      if (this.extrasPickerView) {
        this.extrasPickerView.addEventListener('click', function (e) {
          var swatch = e.target.closest && e.target.closest('[data-rf-control="extra-variant"]');
          if (!swatch || swatch.disabled || !e.detail) return;
          // After the change event has written the colour.
          window.setTimeout(function () {
            self.closeExtrasPicker();
          }, 0);
        });
      }

      // Offered only where it can actually work. A clipboard write needs a
      // secure context, so on plain http the button never appears rather than
      // appearing and failing.
      var continueBtn = this.querySelector('[data-rf-continue]');
      if (continueBtn) {
        // SHOP-165: adds the build, then leaves the flow (no checkout).
        continueBtn.addEventListener('click', function () {
          self.addToCart({ redirect: self.config.continueUrl });
        });
      }

      if (this.shareBtn && navigator.clipboard && window.isSecureContext) {
        this.shareBtn.hidden = false;
        this.shareBtn.addEventListener('click', function () {
          self.copyBuildLink();
        });
      }

      if (this.postcodeInput) {
        // Validate on the fourth digit, not on Confirm — suburb, warehouse and
        // lead time print underneath immediately.
        this.postcodeInput.addEventListener('input', function () {
          this.value = this.value.replace(/\D/g, '').slice(0, 4);
          if (self.postcodeDetected) self.postcodeDetected.hidden = true;
          self.validatePostcode(this.value);
        });
        this.postcodeInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (self.postcodeConfirm && !self.postcodeConfirm.disabled) self.postcodeConfirm.click();
          }
        });
      }

      if (this.postcodeConfirm) {
        this.postcodeConfirm.addEventListener('click', function (e) {
          e.preventDefault();
          self.confirmPostcode();
        });
      }

      this.querySelectorAll('[data-rf-start-build]').forEach(function (el) {
        el.addEventListener('click', function (e) {
          e.preventDefault();
          self.startBuild();
        });
      });

      this.querySelectorAll('[data-rf-change-postcode]').forEach(function (el) {
        el.addEventListener('click', function (e) {
          e.preventDefault();
          self.showPostcodeView('ask');
          if (self.postcodeInput) self.postcodeInput.focus();
        });
      });

      // Desktop previews a prop-kit colourway on hover and commits it on the
      // tick (the mobile board has no hover, so selection is the only trigger
      // there). An out-of-stock colourway never previews.
      if (media('(hover: hover) and (pointer: fine)')) {
        this.querySelectorAll('[data-rf-control="propKit"]').forEach(function (input) {
          var row = input.closest('.rf-swatch, .rf-option');
          if (!row || input.disabled) return;
          row.addEventListener('mouseenter', function () {
            self.previewPropKit = input.value;
            self.renderScene();
          });
          row.addEventListener('mouseleave', function () {
            self.previewPropKit = null;
            self.renderScene();
          });
        });
      }

      // Option changes
      this.addEventListener('change', function (e) {
        var name = e.target.getAttribute('data-rf-control');
        if (!name) return;
        self.onControlChange(name, e.target);
      });

      // A colour picked on the page's own swatches, with a build already
      // started, replaces the build's upholstery, so reopening shows what the
      // customer last chose rather than a stale colour. Before any build the
      // swatches still change the hero only. An option this warehouse cannot
      // ship is still taken: the page already shows it, so the build follows
      // and the stock notice says it cannot be delivered and blocks the add.
      // The page's current variant is always tracked, build or not: quick add
      // builds whatever the customer is looking at.
      this.pageVariant = this.config.defaultUpholstery;
      if (this.planPills) {
        this.planPills.addEventListener('click', function (e) {
          var pill = e.target.closest('.plan-pill, .plan-sub-pill');
          if (pill) self.pickPlanFromPill(pill);
        });
      }
      document.addEventListener('variantChanged', function (e) {
        var variantId = e.detail && e.detail.variantId;
        if (variantId) self.pageVariant = String(variantId);
        if (!variantId || String(variantId) === String(self.state.upholstery)) return;
        // No radio when each frame has one colour: the frame step is the
        // only control, and the variant is still one the build offers.
        var radio = self.querySelector('[data-rf-control="upholstery"][value="' + variantId + '"]');
        if (!radio && !self.findUpholstery(variantId)) return;
        if (radio) radio.checked = true;
        if (self.state.stepIndex > 0) {
          if (radio) radio.dispatchEvent(new Event('change', { bubbles: true }));
          else self.onControlChange('upholstery', { value: String(variantId) });
          return;
        }
        // Not started yet: take the colour quietly so the build opens on it
        // (SHOP-149), without announcing or redrawing a render nobody can see.
        self.state.upholstery = String(variantId);
        self.syncFrame();
        self.syncDefaultExtraColours();
        self.persist();
        self.updateTotals();
      });
      // After the handler above has recorded the page's colour, the pills
      // follow it (SHOP-154), and so does a plan's deposit and per-week price:
      // a frame can carry its own.
      document.addEventListener('variantChanged', function () {
        self.syncBuyBoxPrice();
      });

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.goNext();
        });
      }

      if (this.backBtn) {
        this.backBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.goBack();
        });
      }

      if (this.addBtn) {
        this.addBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.addToCart({ checkout: true });
        });
      }

      if (this.summaryList) {
        this.summaryList.addEventListener('click', function (e) {
          var remove = e.target.closest('[data-rf-remove]');
          if (remove) {
            e.preventDefault();
            self.removeExtraFromSummary(remove);
            return;
          }
          var btn = e.target.closest('[data-rf-edit]');
          if (!btn) return;
          e.preventDefault();
          self.goToStep(btn.getAttribute('data-rf-edit'), { returnToSummary: true });
        });
      }

      // The card is not a dialog, but Esc should still get you out of the flow.
      if (this.buildPanel) {
        this.buildPanel.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && !self.buildPanel.modal) self.exitBuild();
        });
        this.buildPanel.addEventListener('rf:panel-close', function () {
          self.teardownRender();
          // Card shut: the parameters have nothing left to describe.
          self.syncUrl();
        });
        this.buildPanel.addEventListener('rf:panel-open', function () {
          self.syncUrl();
        });
      }
    }

    /* ---- persistence --------------------------------------------------
       Held on the session so reopening resumes where you were and checkout
       never asks for the postcode twice. */

    restore() {
      var base = {
        postcode: '',
        zone: '',
        region: '',
        serviceable: null,
        upholstery: this.config.defaultUpholstery || null,
        propKit: null,
        payment: this.config.defaultPayment || null,
        extras: {},
        inclusionPicks: {},
        stepIndex: 0
      };
      try {
        var raw = window.sessionStorage.getItem(this.storageKey);
        if (raw) {
          var saved = JSON.parse(raw);
          Object.keys(base).forEach(function (k) {
            if (saved[k] !== undefined && saved[k] !== null) base[k] = saved[k];
          });
        }
      } catch (e) {
        /* private mode — the flow simply does not resume */
      }
      return base;
    }

    persist() {
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
        if (this.state.postcode) {
          // Held beyond the session so a returning customer sees a receipt
          // line rather than an empty form, and checkout never asks twice.
          window.localStorage.setItem(this.postcodeKey, this.state.postcode);
        }
      } catch (e) {
        /* ignore */
      }
    }

    /** A guess, shown with a quiet "detected" label so it reads as something
     *  the customer can change. `window.yr.detectedPostcode` is the hook for a
     *  geo-IP lookup; without one we fall back to what they told us last time. */
    detectedPostcode() {
      if (window.yr && window.yr.detectedPostcode) return String(window.yr.detectedPostcode);
      if (this.config.detectedPostcode) return String(this.config.detectedPostcode);
      try {
        return window.localStorage.getItem(this.postcodeKey) || '';
      } catch (e) {
        return '';
      }
    }

    hydrateFromState() {
      if (this.state.postcode && this.postcodeInput) {
        this.postcodeInput.value = this.state.postcode;
        this.validatePostcode(this.state.postcode, { silent: true });
      } else if (this.detectedPostcode() && this.postcodeInput) {
        // Prefilled from geo-IP with a quiet "detected" label, so it reads as
        // a guess the customer can change.
        var detected = this.detectedPostcode();
        this.postcodeInput.value = detected;
        if (this.postcodeDetected) this.postcodeDetected.hidden = false;
        this.validatePostcode(detected, { silent: true });
      }

      if (this.resumeBtn) {
        this.resumeBtn.hidden = !this.state.postcode;
        var label = this.resumeBtn.querySelector('[data-rf-resume-label]');
        if (label && this.state.postcode) {
          label.textContent = 'Ships to ' + this.state.postcode;
        }
      }

      this.applySelectionsToControls();
      this.syncStateFromControls();
      this.updateTotals();
      this.syncBuyBoxPrice();
    }

    /** The markup ships with sensible defaults checked (the current variant,
     *  the first payment plan). Where a restored selection did not match one of
     *  them, the DOM is the truth — read it back so the footer total, the
     *  render and the cart payload can never disagree with what is ticked. */
    syncStateFromControls() {
      var self = this;
      [
        ['upholstery', 'upholstery'],
        ['propKit', 'propKit'],
        ['payment', 'payment']
      ].forEach(function (pair) {
        // No controls, no choice to read back: a Reformer with one colour per
        // frame keeps the variant the frame step (or the page) gave it.
        if (!self.querySelector('[data-rf-control="' + pair[0] + '"]')) return;
        var checked = self.querySelector('[data-rf-control="' + pair[0] + '"]:checked');
        self.state[pair[1]] = checked ? checked.value : null;
      });
      // Same rule for each inclusion step: what is ticked is the truth.
      var picks = {};
      this.steppedInclusionProducts().forEach(function (entry) {
        var checked = self.querySelector('[data-rf-control="inclusion"][data-inclusion="' + entry.id + '"]:checked');
        if (checked) picks[entry.id] = checked.value;
      });
      this.state.inclusionPicks = picks;
      this.persist();
    }

    applySelectionsToControls() {
      var self = this;
      ['upholstery', 'propKit', 'payment'].forEach(function (key) {
        var value = self.state[key];
        if (value === null || value === undefined) return;
        var input = self.querySelector(
          '[data-rf-control="' + key + '"][value="' + String(value).replace(/"/g, '\\"') + '"]'
        );
        if (input && !input.disabled) input.checked = true;
      });

      Object.keys(this.state.inclusionPicks || {}).forEach(function (id) {
        var input = self.querySelector(
          '[data-rf-control="inclusion"][data-inclusion="' + id + '"][value="' + self.state.inclusionPicks[id] + '"]'
        );
        if (input && !input.disabled) input.checked = true;
      });

      Object.keys(this.state.extras || {}).forEach(function (productId) {
        var toggle = self.querySelector('[data-rf-control="extra"][data-product="' + productId + '"]');
        if (toggle) toggle.checked = true;
        var variantId = self.state.extras[productId];
        var variantInput = self.querySelector(
          '[data-rf-control="extra-variant"][data-product="' + productId + '"][value="' + variantId + '"]'
        );
        if (variantInput) variantInput.checked = true;
        self.syncExtraRow(productId);
      });

      this.syncFrame();
    }

    /* ---- entry points ---- */

    start(openedBy) {
      this.quickPending = false;
      // Postcode first: warehouse, lead time and stock all derive from it, so
      // nothing later in the flow can make a promise it cannot keep.
      if (this.state.serviceable === true && this.state.postcode) {
        // The postcode is held on the session, so we never ask twice: the
        // postcode panel shows once, and after that the trigger goes straight
        // into the build (SHOP-161). "Ships to XXXX" under the trigger is the
        // way back to it.
        this.startBuild(openedBy);
        return;
      }
      this.openPostcode(openedBy);
    }

    /** Quick add: the build is filled for them and goes straight into the
     *  cart. Without a postcode it is asked first, as always — stock cannot be
     *  judged without a warehouse — and confirming it carries straight on. */
    quickStart(openedBy) {
      this.quickPending = true;
      if (this.state.serviceable === true && this.state.postcode) {
        this.startBuild(openedBy);
      } else {
        this.openPostcode(openedBy);
      }
    }

    openPostcode(openedBy) {
      var resumed = this.state.serviceable === true && !!this.state.postcode;
      this.showPostcodeView(resumed ? 'served' : 'ask');
      if (resumed) {
        this.fillServed();
        this.preloadPlates();
        this.preloadStock();
      }
      this.postcodePanel.open(openedBy || this.trigger);
      this.announce(this.config.copy.postcodeAnnounce || 'Delivery postcode. Step 1.');
    }

    showPostcodeView(view) {
      var self = this;
      Object.keys(this.postcodeViews).forEach(function (key) {
        var el = self.postcodeViews[key];
        if (el) el.hidden = key !== view;
      });

      // The dialog is named by whichever heading is actually on screen — an
      // aria-labelledby pointing at a hidden heading leaves it nameless.
      var dialog = this.postcodePanel.querySelector('.rf-panel');
      var labelId = dialog && dialog.getAttribute('data-rf-label-' + view);
      if (dialog && labelId) dialog.setAttribute('aria-labelledby', labelId);

      if (this.postcodePanel && this.postcodePanel.updateScrollShadow) {
        this.postcodePanel.updateScrollShadow();
      }
    }

    /* ---- postcode -------------------------------------------------------
       Delivery zones come from the same metaobject the valet checker uses, so
       there is one source of truth for what we can serve. */

    get zoneTable() {
      return (window.yr && window.yr.JSONData && window.yr.JSONData['Valet Locations']) || null;
    }

    lookupPostcode(postcode) {
      var table = this.zoneTable;
      if (!table) return null;

      for (var region in table) {
        if (!Object.prototype.hasOwnProperty.call(table, region)) continue;
        var entry = table[region];
        var valet = entry.valet_zipcodes || [];
        var toDoor = entry.to_door_zipcodes || [];
        if (valet.indexOf(postcode) !== -1) {
          return { region: region, zone: entry.catchment_zone, valet: true };
        }
        if (toDoor.indexOf(postcode) !== -1) {
          return { region: region, zone: entry.catchment_zone, valet: false };
        }
      }
      return null;
    }

    validatePostcode(value, options) {
      options = options || {};
      var valid = /^\d{4}$/.test(value);

      if (this.postcodeError) this.postcodeError.hidden = true;
      if (this.postcodeInput) this.postcodeInput.setAttribute('aria-invalid', 'false');

      if (!valid) {
        if (this.postcodeResolved) this.postcodeResolved.hidden = true;
        this.setConfirmEnabled(false);
        this.state.serviceable = null;
        return;
      }

      if (!this.zoneTable) {
        // The zone table is injected by json-data-loader; if it has not landed
        // yet, let the customer continue and fail honestly at the next step.
        this.showResolved(this.config.copy.zoneUnavailable || 'Checking availability…', true);
        this.setConfirmEnabled(true);
        return;
      }

      var match = this.lookupPostcode(value);
      if (!match) {
        // NZ sells outside the valet area as collection from depot, as the
        // supercycle zipcode checker does, so this is not a dead end.
        var depotLine = this.config.copy.depotLine || 'Collection from depot';
        this.state.serviceable = true;
        this.state.postcode = value;
        this.state.zone = null;
        this.state.region = null;
        this.state.valet = false;
        this.showResolved(depotLine);
        this.setConfirmEnabled(true);
        if (!options.silent) this.announce(value + '. ' + depotLine);
        this.persist();
        return;
      }

      var zoneCopy = (this.config.zones && this.config.zones[match.zone]) || {};
      this.state.serviceable = true;
      this.state.postcode = value;
      this.state.zone = match.zone;
      this.state.region = match.region;
      this.state.valet = match.valet;

      var parts = [];
      parts.push(zoneCopy.label || match.region);
      if (zoneCopy.warehouse) parts.push('ships from ' + zoneCopy.warehouse);
      if (zoneCopy.leadTime) parts.push(zoneCopy.leadTime);
      var line = parts.join(' · ');

      this.showResolved(line);
      this.setConfirmEnabled(true);
      if (!options.silent) this.announce(value + '. ' + line);
      this.persist();
    }

    showResolved(text, pending) {
      if (!this.postcodeResolved) return;
      this.postcodeResolved.textContent = text;
      this.postcodeResolved.hidden = false;
      this.postcodeResolved.classList.toggle('is-pending', !!pending);
    }

    setConfirmEnabled(enabled) {
      if (!this.postcodeConfirm) return;
      this.postcodeConfirm.disabled = !enabled;
    }

    confirmPostcode() {
      var value = this.postcodeInput ? this.postcodeInput.value : '';
      if (!/^\d{4}$/.test(value)) {
        if (this.postcodeError) {
          this.postcodeError.hidden = false;
          this.postcodeError.textContent =
            this.config.copy.postcodeInvalid || 'Enter a 4-digit New Zealand postcode.';
        }
        if (this.postcodeInput) {
          this.postcodeInput.setAttribute('aria-invalid', 'true');
          this.postcodeInput.focus();
        }
        return;
      }

      this.persist();

      if (this.state.serviceable === false) {
        this.showPostcodeView('unserved');
        this.fillUnserved();
        this.announce(this.config.copy.notServedAnnounce || 'We cannot deliver to that postcode yet.');
        this.postcodePanel.focusFirst();
        return;
      }

      this.showPostcodeView('served');
      this.fillServed();
      // A new postcode is a new warehouse: every answer held against the old
      // one is void, and whatever is already chosen has to be re-checked —
      // which is what catches a shared build landing somewhere it cannot ship.
      this.stockCache = {};
      this.stockProblems = null;
      // Clears any notice left over from the previous postcode straight away,
      // before the prefetch has had time to answer for the new one.
      this.updateStockNotice();
      this.preloadStock();
      // Fetch every upholstery render set while this panel is on screen so
      // the first build step never shows a spinner.
      this.preloadPlates();
      this.announce(this.config.copy.servedAnnounce || "Good news, we're ready to ship.");
      // Quick add asked for the postcode only to judge stock; carry straight on.
      if (this.quickPending) {
        this.startBuild();
        return;
      }
      this.postcodePanel.focusFirst();
    }

    fillServed() {
      var el = this.querySelector('[data-rf-served-meta]');
      if (!el) return;
      var depot = this.state.valet === false && !this.state.zone;
      this.querySelectorAll('[data-rf-benefits]').forEach(function (list) {
        list.hidden = list.getAttribute('data-rf-benefits') !== (depot ? 'depot' : 'valet');
      });
      var zoneCopy = (this.config.zones && this.config.zones[this.state.zone]) || {};
      var bits = [this.state.postcode];
      if (depot) bits.push(this.config.copy.depotLine || 'Collection from depot');
      if (zoneCopy.label) bits.push(zoneCopy.label);
      if (zoneCopy.leadTime) bits.push(zoneCopy.leadTime);
      el.textContent = bits.join(' · ');
    }

    fillUnserved() {
      this.querySelectorAll('[data-rf-unserved-postcode]').forEach(
        function (el) {
          el.textContent = this.state.postcode;
        }.bind(this)
      );
      var field = this.querySelector('[data-rf-waitlist-postcode]');
      if (field) field.value = this.state.postcode;
    }

    /* ---- the transition ------------------------------------------------
       Build My Reformer fires two things at once: the scrim lifts and the
       gallery cross-dissolves into the live render. The panel does not move;
       it reshapes into the card. Nothing scrolls. */

    startBuild(openedBy) {
      var self = this;
      if (this.quickPending) {
        this.quickPending = false;
        // The stock lookups can take a moment; the buttons that started it
        // spin until the build opens.
        var busy = [this.quickAddBtn].concat(Array.from(this.querySelectorAll('[data-rf-start-build]')));
        var setBusy = function (on) {
          busy.forEach(function (btn) {
            if (!btn) return;
            btn.classList.toggle('is-loading', on);
            btn.disabled = on;
            if (on) btn.setAttribute('aria-busy', 'true');
            else btn.removeAttribute('aria-busy');
          });
        };
        setBusy(true);
        // Filled → straight into the cart drawer. Anything short of that (a
        // step with nothing to ship, a stock refusal, a failed add) opens the
        // build where the problem is, with its message showing.
        this.quickBuild()
          .then(function (filled) {
            return filled ? self.addToCart() : false;
          })
          .catch(function (e) {
            console.error('[reformer-flow] quick add', e);
            return false;
          })
          .then(function (added) {
            setBusy(false);
            if (added) {
              if (self.postcodePanel.isOpen) self.postcodePanel.close('advance');
              return;
            }
            self.startBuild(openedBy);
          });
        return;
      }
      this.activateRender();

      if (this.postcodePanel.isOpen) {
        this.postcodePanel.close('advance');
      }

      window.setTimeout(
        function () {
          self.goToStep(self.steps[Math.min(self.state.stepIndex, self.steps.length - 1)] || self.steps[0]);
          self.buildPanel.open(openedBy || self.trigger);
        },
        prefersReducedMotion() ? 0 : 120
      );
    }

    exitBuild() {
      this.buildPanel.close('escape');
    }

    activateRender() {
      // The card takes the buy box's place in the same column.
      if (this.sectionEl) this.sectionEl.classList.add('rf-flow-active');
      // On a phone the build owns the screen outright: the section is pinned
      // to the viewport and the site chrome stands down, so the height the
      // flow has to work with is the whole of it rather than whatever is left
      // under a header. The close control is the only way out, which is why
      // it moves to the corner of the screen rather than the corner of a card.
      document.body.classList.add('rf-takeover');
      this.sealPageBelow();
      window.dispatchEvent(new Event('resize'));

      if (!this.stage) return;
      this.stage.show();
      if (this.stageHost) {
        this.stageHost.classList.add('rf-render-active');
        Array.prototype.forEach.call(this.stageHost.children, function (child) {
          if (child === this.stage) return;
          child.setAttribute('aria-hidden', 'true');
          child.setAttribute('inert', '');
        }, this);
      }
      // The thumbnail rail belongs to the editorial gallery, not the render.
      if (this.thumbTrack) this.thumbTrack.classList.add('rf-thumbs-hidden');
      this.renderScene();
    }

    teardownRender() {
      if (this.sectionEl) this.sectionEl.classList.remove('rf-flow-active');
      document.body.classList.remove('rf-takeover');
      this.unsealPageBelow();
      // The buy box is about to be back on screen; it should be showing
      // whatever was chosen in the step that has just closed.
      this.syncBuyBoxPrice();
      window.dispatchEvent(new Event('resize'));

      if (!this.stage) return;
      this.stage.hide();
      if (this.stageHost) {
        this.stageHost.classList.remove('rf-render-active');
        Array.prototype.forEach.call(this.stageHost.children, function (child) {
          if (child === this.stage) return;
          child.removeAttribute('aria-hidden');
          child.removeAttribute('inert');
        }, this);
      }
      if (this.thumbTrack) this.thumbTrack.classList.remove('rf-thumbs-hidden');
    }

    /** While the build is open the flow owns the screen: the render is the
     *  content and the card is the only thing to do. Everything below it —
     *  the inclusions module, the description tabs, and every section after
     *  the product section — is taken out of the page so it cannot be
     *  scrolled to and cannot be reached by Tab either. Hiding rather than
     *  locking the scroll is what lets the mobile card keep scrolling with
     *  the render, which is how the board draws it. */
    sealPageBelow() {
      if (this.sealed) return;
      this.sealed = [];

      var seal = function (el) {
        if (!el || el.classList.contains('rf-flow-sealed')) return;
        el.classList.add('rf-flow-sealed');
        el.setAttribute('inert', '');
        this.sealed.push(el);
      }.bind(this);

      // Inside the product section: the inclusions and description block.
      var below = document.querySelector('.inclusions-and-information-container');
      seal(below);

      // Everything after the product section.
      if (this.sectionEl && this.sectionEl.parentElement) {
        var node = this.sectionEl.nextElementSibling;
        while (node) {
          if (node.tagName !== 'SCRIPT' && node.tagName !== 'STYLE') seal(node);
          node = node.nextElementSibling;
        }
      }

      // The page is now about a screen tall, so a scroll position taken from
      // the old height would leave the card off-screen.
      var top = this.sectionEl ? this.sectionEl.getBoundingClientRect().top : 0;
      if (top < 0) {
        window.scrollBy({ top: top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }

    unsealPageBelow() {
      if (!this.sealed) return;
      this.sealed.forEach(function (el) {
        el.classList.remove('rf-flow-sealed');
        el.removeAttribute('inert');
      });
      this.sealed = null;
    }

    preloadPlates() {
      if (!this.stage) return;
      var urls = [];
      var key = isDesktop() ? 'desktop' : 'mobile';
      (this.config.upholstery || []).forEach(function (option) {
        if (option.plates) {
          urls.push(option.plates[key] || option.plates.desktop);
          if (option.plates.accessories) urls.push(option.plates.accessories);
        }
      });
      if (this.config.propKit) {
        (this.config.propKit.options || []).forEach(function (option) {
          if (option.plate) urls.push(option.plate);
        });
      }
      (this.config.inclusions || []).forEach(function (inc) {
        (inc.options || []).forEach(function (option) {
          if (option.plate) urls.push(option.plate);
        });
      });
      if (this.config.basePlate) urls.push(this.config.basePlate);
      this.stage.preload(urls.filter(Boolean));
    }

    /** Placements are percentages of the scene, a fixed-ratio box, so one set
     *  serves every breakpoint and mobile shows the same composite as desktop. */
    placementFor(layer) {
      return (this.config.placement || {})[layer];
    }

    renderScene() {
      if (!this.stage) return;
      var key = isDesktop() ? 'desktop' : 'mobile';
      this.sceneBreakpoint = key;
      var uph = this.findUpholstery(this.state.upholstery);

      this.stage.setLayer('base', this.config.basePlate || null);

      if (uph) {
        // A cut-out is composed into the scene: it sits in the upper part of
        // the stage so the included accessories have ground to stand on
        // beneath it. A lifestyle-photo fallback has nothing to compose with,
        // so it just fills the stage.
        this.stage.setLayer(
          'upholstery',
          (uph.plates && (uph.plates[key] || uph.plates.desktop)) || uph.image,
          uph.cutOut === false
            ? { cutOut: false }
            : { placement: this.placementFor('upholstery'), cutOut: true }
        );
        this.stage.classList.toggle('rf-stage--photo', !uph.cutOut);
      }

      // The jumpboard and box are included in the order and change colour with
      // the upholstery, so they appear in the scene as soon as a colour is
      // chosen — the customer is looking at exactly what gets delivered.
      var jumpboard = this.resolveInclusion(this.findInclusion('jumpboard'));
      var jumpboardPlate =
        (uph && uph.plates && uph.plates.accessories) || (jumpboard && jumpboard.plate) || null;
      this.stage.setLayer('accessories', jumpboardPlate, {
        placement: this.placementFor('accessories'),
        cutOut: uph && uph.plates && uph.plates.accessories ? true : jumpboard && jumpboard.cutOut
      });

      // Props enter the scene at the step that introduces them and stay from
      // there on — they must not appear over the upholstery step just because
      // a colourway is sitting in the session from an earlier visit, or
      // because the customer stepped back. An out-of-stock colourway never
      // enters the scene at all.
      // A hovered colourway previews in place of the committed one.
      var prop = this.findPropKit(this.previewPropKit || this.state.propKit);
      var propsStep = this.steps.indexOf('props');
      var propsReached = propsStep !== -1 && this.state.stepIndex >= propsStep;
      if (this.previewPropKit && this.currentStep !== 'props') {
        this.previewPropKit = null;
        prop = this.findPropKit(this.state.propKit);
      }
      this.stage.setLayer('props', propsReached && prop && prop.available ? prop.plate : null, {
        placement: this.placementFor('props'),
        cutOut: prop ? prop.cutOut : true
      });

      this.stage.setCaption(this.config.copy.renderCaption || '');
    }

    findUpholstery(id) {
      return (this.config.upholstery || []).filter(function (o) {
        return String(o.variantId) === String(id);
      })[0];
    }

    findPropKit(id) {
      if (!this.config.propKit) return null;
      return (this.config.propKit.options || []).filter(function (o) {
        return String(o.variantId) === String(id);
      })[0];
    }

    findPayment(value) {
      return this.paymentPlans().filter(function (o) {
        return o.value === value;
      })[0];
    }

    /** The chosen variant's plans: a frame can carry its own price, deposit
     *  and plans. The page variant's plans when the variant has none listed.
     *  shortcut: the payment step's radios come from the page variant, so
     *  every variant is assumed to offer the same plans; if Walnut ever has
     *  different ones, render the radios per variant. */
    paymentPlans() {
      var uph = this.findUpholstery(this.state.upholstery);
      return (uph && uph.payment && uph.payment.length ? uph.payment : this.config.payment) || [];
    }

    /** The payment step's figures follow the chosen variant. Same rule as the
     *  Liquid: the YR Payment Plan shows its deposit, the rest the price. */
    syncPaymentAmounts() {
      var self = this;
      this.querySelectorAll('[data-rf-pay-amount]').forEach(function (el) {
        var plan = self.findPayment(el.getAttribute('data-rf-pay-amount'));
        if (!plan) return;
        el.textContent = self.money(plan.value === 'YR_PAYMENT_PLAN' && plan.deposit ? plan.deposit : plan.amount);
      });
    }

    /* ---- frame ----------------------------------------------------------
       Not stored on its own: the frame is the chosen variant's, so a saved
       build, a share link and the PDP swatches all carry it already. */

    /** The frames on offer, in variant order. Empty without a Frame option. */
    frames() {
      var seen = [];
      (this.config.upholstery || []).forEach(function (o) {
        if (o.frame && seen.indexOf(o.frame) === -1) seen.push(o.frame);
      });
      return seen;
    }

    /** Whether a colourway can be chosen: its radio, once the warehouse check
     *  has marked it, else the shop-wide flag. */
    canShipUpholstery(option) {
      var input = this.querySelector('[data-rf-control="upholstery"][value="' + option.variantId + '"]');
      return input ? !input.disabled : option.available !== false;
    }

    /** Moving frame keeps the colourway where that frame has it, else takes
     *  the frame's first that can ship, else its first. */
    variantForFrame(frame) {
      var self = this;
      var current = this.findUpholstery(this.state.upholstery);
      var options = (this.config.upholstery || []).filter(function (o) {
        return o.frame === frame;
      });
      var usable = options.filter(function (o) {
        return self.canShipUpholstery(o);
      });
      var same = current
        ? usable.filter(function (o) {
            return o.colourKey === current.colourKey;
          })[0]
        : null;
      return same || usable[0] || options[0];
    }

    /** The frame radios and the upholstery list follow the chosen variant. A
     *  frame with nothing that can ship is unavailable, like any option. */
    syncFrame() {
      var self = this;
      var uph = this.findUpholstery(this.state.upholstery);
      var frame = uph ? uph.frame : '';
      this.querySelectorAll('[data-rf-control="frame"]').forEach(function (input) {
        var open = (self.config.upholstery || []).some(function (o) {
          return o.frame === input.value && self.canShipUpholstery(o);
        });
        input.checked = input.value === frame;
        input.disabled = !open;
        var row = input.closest('.rf-option');
        if (row) row.classList.toggle('is-unavailable', !open);
      });
      if (frame) {
        this.querySelectorAll('.rf-option[data-rf-frame]').forEach(function (row) {
          row.hidden = row.getAttribute('data-rf-frame') !== frame;
        });
      }
      this.syncPaymentAmounts();
    }

    /* ---- inclusion products ---------------------------------------------
       The active inclusion's Products-list entries that go in the cart
       (add_to_cart_on_product_add). Each is either a step of its own
       (hasStep) or added silently with a default variant. */

    findInclusionProduct(id) {
      return (this.config.inclusionProducts || []).filter(function (e) {
        return String(e.id) === String(id);
      })[0];
    }

    steppedInclusionProducts() {
      return (this.config.inclusionProducts || []).filter(function (e) {
        return e.hasStep && e.options && e.options.length > 1;
      });
    }

    /** The option this build commits to for an inclusion product: the
     *  customer's pick where it has a step (none until they choose), else the
     *  upholstery-colour match, else the first in stock, else the first. */
    resolveInclusionProduct(entry) {
      var options = (entry && entry.options) || [];
      if (!options.length) return null;
      if (entry.hasStep && options.length > 1) {
        var pick = (this.state.inclusionPicks || {})[entry.id];
        return (
          options.filter(function (o) {
            return String(o.variantId) === String(pick);
          })[0] || null
        );
      }
      var uph = this.findUpholstery(this.state.upholstery);
      var match = uph
        ? options.filter(function (o) {
            return o.available !== false && o.colourKey && o.colourKey === uph.colourKey;
          })[0]
        : null;
      return (
        match ||
        options.filter(function (o) {
          return o.available !== false;
        })[0] ||
        options[0]
      );
    }

    /** Fills every required choice for quick add. Upholstery is the page's
     *  currently selected variant;
     *  each colourway step takes the colour match, else (Highest Stock mode)
     *  the variant with the most stock shop-wide, else the first — only ever
     *  from what can ship. Pay in full unless a plan was picked on the
     *  PDP pills, no extras. A step with nothing to give
     *  is left empty and this returns false, so the build opens on it. */
    async quickBuild() {
      var self = this;
      if (this.pageVariant && this.findUpholstery(this.pageVariant)) {
        this.state.upholstery = String(this.pageVariant);
      }
      var uph = this.findUpholstery(this.state.upholstery);
      var locationId = this.stockReady ? await this.resolveLocationId() : null;
      var byStock = this.config.allocationMode !== 'first_available_variant';

      // perWarehouse: the prop kit is checked against this postcode's
      // warehouse; inclusion products only on the shop-wide flag, as in
      // auditSelection.
      var pick = async function (options, perWarehouse) {
        var ok = options.filter(function (o) {
          return o.available !== false;
        });
        if (perWarehouse && locationId) {
          var stock = await self.stockForMany(ok.map(function (o) { return String(o.variantId); }), locationId);
          ok = ok.filter(function (o) {
            var answer = stock[String(o.variantId)];
            return !answer || (answer.available && !answer.comingSoon);
          });
        }
        var match = uph
          ? ok.filter(function (o) {
              return o.colourKey && o.colourKey === uph.colourKey;
            })[0]
          : null;
        if (match) return match;
        if (byStock) {
          ok = ok.slice().sort(function (a, b) {
            return (b.inventory || 0) - (a.inventory || 0);
          });
        }
        return ok[0] || null;
      };

      this.clearState();
      var gap = null;

      var props = this.config.propKit && this.config.propKit.options;
      if (props && props.length > 1) {
        var prop = await pick(props, true);
        this.state.propKit = prop ? String(prop.variantId) : null;
        if (!prop) gap = gap || 'props';
      }

      var stepped = this.steppedInclusionProducts();
      for (var i = 0; i < stepped.length; i++) {
        var option = await pick(stepped[i].options, false);
        if (option) this.state.inclusionPicks[stepped[i].id] = String(option.variantId);
        else gap = gap || 'inc-' + stepped[i].id;
      }

      // A plan picked on the PDP pills is what quick add buys (SHOP-154);
      // otherwise it pays in full.
      var plans = this.paymentPlans();
      if (plans.length > 1 && !(this.state.paymentPicked && this.findPayment(this.state.payment))) {
        var full = plans.filter(function (p) {
          return !p.isPlan;
        })[0] || plans[0];
        this.state.payment = full.value;
      }

      this.applySelectionsToControls();
      this.renderScene();
      this.state.stepIndex = Math.max(0, this.steps.indexOf(gap || 'summary'));
      this.persist();
      this.syncUrl();
      return !gap;
    }

    findInclusion(key) {
      return (this.config.inclusions || []).filter(function (i) {
        return i.key === key;
      })[0];
    }

    /** Inclusions that ship in the Reformer's colourway (the jumpboard and
     *  box) follow the upholstery choice rather than being picked separately —
     *  the customer is looking at exactly what gets delivered. */
    resolveInclusion(inc) {
      if (!inc) return null;
      if (!inc.colourMatched || !inc.options || !inc.options.length) {
        return { variantId: inc.variantId, label: '', plate: null, cutOut: true, saving: inc.saving || 0 };
      }
      var uph = this.findUpholstery(this.state.upholstery);
      var match = uph
        ? inc.options.filter(function (o) {
            return o.colourKey && o.colourKey === uph.colourKey;
          })[0]
        : null;
      var chosen =
        match ||
        inc.options.filter(function (o) {
          return o.available;
        })[0] ||
        inc.options[0];
      return {
        variantId: chosen.variantId,
        label: chosen.label,
        plate: chosen.plate || null,
        cutOut: chosen.cutOut !== false,
        saving: chosen.saving || 0
      };
    }

    /* ---- controls ---- */

    onControlChange(name, input) {
      if (name === 'frame') {
        var swap = this.variantForFrame(input.value);
        if (swap) this.state.upholstery = String(swap.variantId);
        this.syncFrame();
        this.renderScene();
        var onFrame = this.findUpholstery(this.state.upholstery);
        this.announce('Frame: ' + input.value + (onFrame ? ', ' + onFrame.label : '') + '.');
        this.syncDefaultExtraColours();
        this.syncDefaultPropKit();
      } else if (name === 'upholstery') {
        this.state.upholstery = input.value;
        // A PDP swatch can land on another frame.
        this.syncFrame();
        this.renderScene();
        var uph = this.findUpholstery(input.value);
        // Every render change has a text equivalent — colour is never carried
        // by the image alone.
        this.announce((this.config.copy.upholsteryAnnounce || 'Upholstery: {name}.').replace('{name}', uph ? uph.label : ''));
        this.syncDefaultExtraColours();
        this.syncDefaultPropKit();
      } else if (name === 'propKit') {
        this.previewPropKit = null;
        this.state.propKit = input.value;
        this.state.propKitPicked = true;
        this.renderScene();
        var prop = this.findPropKit(input.value);
        this.announce(
          (this.config.copy.propAnnounce || 'Prop starter kit: {name}.').replace('{name}', prop ? prop.label : '')
        );
      } else if (name === 'payment') {
        this.state.payment = input.value;
        this.state.paymentPicked = true;
        var plan = this.findPayment(input.value);
        this.announce((this.config.copy.paymentAnnounce || 'Payment: {name}.').replace('{name}', plan ? plan.label : ''));
      } else if (name === 'extra') {
        var productId = input.getAttribute('data-product');
        if (input.checked) {
          // Defaulted items are shown in the same colourway as the chosen
          // upholstery where the accessory has one.
          this.state.extras[productId] =
            this.defaultExtraVariant(productId) || input.getAttribute('data-variant');
        } else {
          delete this.state.extras[productId];
          delete this.explicitExtraPicks[productId];
        }
        this.syncExtraRow(productId);

        // Adding something that comes in colours is a choice, so it opens the
        // choice. The upholstery-matched default is already ticked, which
        // makes Confirm a single tap for anyone who does not want to change it.
        if (input.checked && input.hasAttribute('data-rf-has-colours')) {
          this.openExtrasPicker(productId, input);
        }
      } else if (name === 'inclusion') {
        var incId = input.getAttribute('data-inclusion');
        if (!this.state.inclusionPicks) this.state.inclusionPicks = {};
        this.state.inclusionPicks[incId] = input.value;
        var incEntry = this.findInclusionProduct(incId);
        var incOption = this.resolveInclusionProduct(incEntry);
        if (incEntry && incOption) this.announce(incEntry.title + ': ' + incOption.label + '.');
      } else if (name === 'extra-variant') {
        var pid = input.getAttribute('data-product');
        this.state.extras[pid] = input.value;
        // Once a colour is picked by hand, changing the upholstery must not
        // quietly overwrite it.
        this.explicitExtraPicks[pid] = true;
      }

      this.persist();
      this.updateTotals();
      this.syncUrl();
    }

    defaultExtraVariant(productId) {
      var extra = (this.config.extras || []).filter(function (e) {
        return String(e.productId) === String(productId);
      })[0];
      if (!extra || !extra.variants || !extra.variants.length) return null;

      // Defaulted items appear in the same colourway as the chosen upholstery
      // where one exists.
      var uph = this.findUpholstery(this.state.upholstery);
      var match = uph
        ? extra.variants.filter(function (v) {
            return v.colourKey && uph.colourKey && v.colourKey === uph.colourKey;
          })[0]
        : null;
      return (match || extra.variants[0]).variantId;
    }

    syncDefaultExtraColours() {
      var self = this;
      Object.keys(this.state.extras).forEach(function (productId) {
        var current = self.state.extras[productId];
        var extra = (self.config.extras || []).filter(function (e) {
          return String(e.productId) === String(productId);
        })[0];
        if (!extra || !extra.variants || extra.variants.length < 2) return;
        // Only re-colour an extra the customer has not explicitly re-picked.
        if (self.explicitExtraPicks && self.explicitExtraPicks[productId]) return;
        var next = self.defaultExtraVariant(productId);
        if (next && next !== current) {
          self.state.extras[productId] = next;
          var input = self.querySelector(
            '[data-rf-control="extra-variant"][data-product="' + productId + '"][value="' + next + '"]'
          );
          if (input) input.checked = true;
        }
      });
    }

    /** The prop kit in the upholstery's colour is ticked and listed first, until
     *  the customer picks one themselves (SHOP-156). Only a colour this
     *  warehouse can ship is taken; otherwise the step waits for a choice. */
    syncDefaultPropKit() {
      if (this.state.propKitPicked) return;
      var options = (this.config.propKit && this.config.propKit.options) || [];
      var uph = this.findUpholstery(this.state.upholstery);
      if (options.length < 2 || !uph || !uph.colourKey) return;
      var match = options.filter(function (o) {
        return o.colourKey === uph.colourKey;
      })[0];
      var input = match && this.querySelector('[data-rf-control="propKit"][value="' + match.variantId + '"]');
      if (!input || input.disabled) return;
      input.checked = true;
      this.state.propKit = String(match.variantId);
      var row = input.closest('.rf-swatch, .rf-option');
      if (row && row.parentNode.firstElementChild !== row) row.parentNode.insertBefore(row, row.parentNode.firstElementChild);
      this.persist();
    }

    /** Keeps one extra's colourway honest: the right radio ticked in the
     *  picker. The list row is just the product; ticking it opens the picker. */
    syncExtraRow(productId) {
      var picker = this.querySelector('[data-rf-extra-picker][data-product="' + productId + '"]');
      var toggle = this.querySelector('[data-rf-control="extra"][data-product="' + productId + '"]');
      var on = !!(toggle && toggle.checked);

      if (!picker) return;

      var wanted = this.state.extras[productId];
      var target = wanted
        ? picker.querySelector('input[value="' + String(wanted).replace(/"/g, '\\"') + '"]')
        : null;
      if (!target) {
        target = picker.querySelector('input:checked') || picker.querySelector('input:not(:disabled)');
      }
      if (target) {
        target.checked = true;
        // Only a ticked extra is in the build; writing it back when unticked
        // re-added a removed colour extra to the summary and cart (SHOP-140).
        if (on) this.state.extras[productId] = target.value;
      }
    }

    /* ---- the extras colourway picker ----
       A second view inside the extras step, not a step of its own: the
       progress indicator does not move, and the footer's Next becomes
       Confirm. Leaving the step at all closes it. */

    openExtrasPicker(productId, openedBy) {
      var picker = this.querySelector('[data-rf-extra-picker][data-product="' + productId + '"]');
      if (!picker || !this.extrasList || !this.extrasPickerView) return;

      this.syncExtraRow(productId);

      // The upholstery's colourway leads the list (SHOP-157).
      var lead = picker.querySelector(
        '[data-rf-control="extra-variant"][value="' + this.defaultExtraVariant(productId) + '"]'
      );
      var leadRow = lead && lead.closest('.rf-swatch');
      if (leadRow && leadRow.parentNode.firstElementChild !== leadRow) {
        leadRow.parentNode.insertBefore(leadRow, leadRow.parentNode.firstElementChild);
      }

      this.querySelectorAll('[data-rf-extra-picker]').forEach(function (p) {
        p.hidden = p !== picker;
      });

      this.extrasPickerProduct = productId;
      this.extrasPickerOpener = openedBy || null;
      this.extrasList.hidden = true;
      this.extrasPickerView.hidden = false;
      this.slideExtrasView(this.extrasPickerView, 'forward');
      this.syncExtrasFooter();

      var heading = picker.querySelector('[data-rf-picker-heading]');
      if (heading) {
        this.focusSoon(heading);
        this.announce(heading.textContent);
      }
      if (this.buildPanel && this.buildPanel.updateScrollShadow) this.buildPanel.updateScrollShadow();
    }

    closeExtrasPicker() {
      if (!this.extrasPickerProduct || !this.extrasList || !this.extrasPickerView) return;

      var productId = this.extrasPickerProduct;
      var opener = this.extrasPickerOpener;
      this.extrasPickerProduct = null;
      this.extrasPickerOpener = null;

      this.extrasPickerView.hidden = true;
      this.extrasList.hidden = false;
      this.slideExtrasView(this.extrasList, 'back');
      this.syncExtraRow(productId);
      this.syncExtrasFooter();
      this.updateTotals();

      // Back to the control that opened it, or to the row it belongs to —
      // never to the top of the card.
      var back =
        (opener && !opener.hidden && opener.offsetParent !== null && opener) ||
        this.querySelector('[data-rf-control="extra"][data-product="' + productId + '"]');
      if (back) this.focusSoon(back);
      if (this.buildPanel && this.buildPanel.updateScrollShadow) this.buildPanel.updateScrollShadow();
    }

    /** The footer is shared, so while the picker is up it has to say what it
     *  will do: Confirm, which closes the picker rather than advancing. */
    syncExtrasFooter() {
      var open = !!this.extrasPickerProduct;
      if (this.nextBtn && !this.nextBtn.hidden) {
        var label = this.nextBtn.querySelector('[data-rf-next-label]');
        if (label) {
          if (open) {
            label.textContent = this.config.copy.confirmLabel || 'Confirm';
          } else {
            var number = this.steps.indexOf(this.currentStep) + 1;
            label.textContent =
              this.steps[number] === 'summary'
                ? this.config.copy.reviewLabel || 'Review Total'
                : this.config.copy.nextLabel || 'Next';
          }
        }
      }
      if (this.backBtn) {
        var backLabel = this.backBtn.querySelector('[data-rf-back-label]');
        if (backLabel) {
          backLabel.textContent = open
            ? this.config.copy.backToExtrasLabel || 'Back to extras'
            : this.config.copy.backLabel || 'Back';
        }
      }
    }

    /** The slide. Short, directional and purely decorative — the view is
     *  already in the right state before it runs, so a browser that skips the
     *  animation loses nothing. */
    slideExtrasView(view, direction) {
      if (!view || prefersReducedMotion()) return;
      view.classList.remove('rf-extras__view--in-forward', 'rf-extras__view--in-back');
      // Reading offsetWidth restarts the animation when the same view slides
      // twice in a row.
      void view.offsetWidth;
      view.classList.add(direction === 'back' ? 'rf-extras__view--in-back' : 'rf-extras__view--in-forward');
    }

    focusSoon(el) {
      window.requestAnimationFrame(
        function () {
          if (!el || el.closest('[hidden]')) return;
          if (this.buildPanel && !this.buildPanel.isOpen) return;
          try {
            el.focus({ preventScroll: true });
          } catch (e) {
            el.focus();
          }
        }.bind(this)
      );
    }

    /* ---- stock, per warehouse ------------------------------------------
       `variant.available` is shop-wide. Stock is not: it sits in a state
       warehouse, and which one serves this order comes from the postcode. The
       YR Original in Espresso is the standing example — available for sale,
       one unit in the country, and it is in Queensland. To anyone in Perth it
       is sold out, and the buy box has always said so because it asks the
       availability service rather than reading the flag.

       The flow asks the same service, so the two can never disagree.

       Checks are cached per variant-and-location and run when a step opens,
       which is also the moment a shared build gets audited against whatever
       postcode the person opening it supplied.

       A failed lookup leaves the option alone rather than disabling it. The
       service is a third party; a blip must not take every option off the
       page, and the cart is still the backstop. */

    get stockReady() {
      return !!(this.config.availabilityApi && this.config.shopDomain);
    }

    /** The warehouse serving this postcode, as an id the service understands.
     *  The zone gives a group name ("VIC/TAS"); the service gives the ids. */
    async resolveLocationId() {
      if (!this.stockReady) return null;
      var self = this;

      var name = this.locationValue();
      if (!name) return null;
      if (this.locationIds && this.locationIds[name] !== undefined) return this.locationIds[name];

      if (!this.locationIdsPromise) {
        var url = this.config.availabilityApi + '/locations?shop=' + encodeURIComponent(this.config.shopDomain);
        this.locationIdsPromise = fetch(url)
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var map = {};
            (data && data.locations ? data.locations : []).forEach(function (l) {
              map[l.name] = l.id;
            });
            return map;
          })
          .catch(function () {
            // Not cached: the next ask (an add to cart retry) tries again.
            self.locationIdsPromise = null;
            return {};
          });
      }

      this.locationIds = await this.locationIdsPromise;
      return this.locationIds[name] !== undefined ? this.locationIds[name] : null;
    }

    /** One lookup per variant per location, held for the life of the page.
     *  Returns `null` where the answer is not known — which is treated as "do
     *  not interfere", never as "unavailable". */
    stockFor(variantId, locationId) {
      var key = variantId + '@' + locationId;
      this.stockCache = this.stockCache || {};
      // The promise is what is cached, not the answer. The prefetch and a step
      // opening will ask for the same variant at the same moment; holding the
      // promise means they share one request instead of racing to make two.
      if (this.stockCache[key] !== undefined) return this.stockCache[key];

      var url =
        this.config.availabilityApi +
        '/availability?shop=' + encodeURIComponent(this.config.shopDomain) +
        '&variant=' + encodeURIComponent(variantId) +
        '&location=' + encodeURIComponent(locationId);

      var pending = fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (!data || typeof data !== 'object') return null;
          return {
            // Pre-order is a yes with a different button label, which is how
            // the buy box has always read it.
            available: !!(data.available || data.preOrderAvailable),
            comingSoon: !!data.isComingSoon,
            note: data.preOrderMessage || ''
          };
        })
        .catch(function () {
          return null;
        });

      this.stockCache[key] = pending;
      return pending;
    }

    /** Warms every answer the build could need, the moment the postcode
     *  resolves — the same bet preloadPlates makes with the render images.
     *  By the time a step is reached its lookups are already in hand, so the
     *  options never redraw under the customer.
     *
     *  Nothing waits on this. It is started and left alone. */
    async preloadStock() {
      if (!this.stockReady) return;
      var locationId = await this.resolveLocationId();
      if (!locationId) return;

      var self = this;
      var unique = function (list) {
        return list.filter(function (id, i, all) {
          return id && all.indexOf(id) === i;
        });
      };

      // The Reformer's colourways, the prop kit's, and anything already
      // chosen. Small enough to settle in one or two round trips, so the
      // first card is drawn from answers already in hand.
      var wanted = unique(
        this.variantsForStep('upholstery')
          .concat(this.variantsForStep('props'))
          .concat(this.selectedVariantIds())
      );
      await this.inBatches(wanted, 8, function (id) {
        return self.stockFor(id, locationId);
      });

      // Anything already chosen — a restored session, or a shared build — is
      // now answerable without a round trip.
      this.updateStockNotice();

    }

    /** A ceiling on how many lookups are in the air at once. This is a
     *  prefetch against a third-party service; there is no one waiting on it,
     *  so there is no reason to open thirty sockets to get it. */
    async inBatches(items, size, fn) {
      var queue = items.slice();
      var runners = [];
      var count = Math.min(size, queue.length);

      for (var i = 0; i < count; i++) {
        runners.push(
          (async function () {
            while (queue.length) {
              try {
                await fn(queue.shift());
              } catch (e) {
                /* a prefetch failing is not an event */
              }
            }
          })()
        );
      }

      await Promise.all(runners);
    }

    async stockForMany(variantIds, locationId) {
      var self = this;
      var unique = variantIds.filter(function (id, i, all) {
        return id && all.indexOf(id) === i;
      });
      var results = await Promise.all(
        unique.map(function (id) {
          return self.stockFor(id, locationId).then(function (r) {
            return { variantId: id, stock: r };
          });
        })
      );
      var out = {};
      results.forEach(function (r) {
        out[r.variantId] = r.stock;
      });
      return out;
    }

    /** Which variants a step puts on screen. */
    variantsForStep(step) {
      // The frame step offers the same variants, a frame at a time.
      if (step === 'upholstery' || step === 'frame') {
        return (this.config.upholstery || []).map(function (o) { return String(o.variantId); });
      }
      if (step === 'props') {
        return ((this.config.propKit && this.config.propKit.options) || []).map(function (o) {
          return String(o.variantId);
        });
      }
      // Extras are deliberately absent. They ship from YR Accessory
      // Fulfillment, which the buy box's own location list filters out, so
      // they hold no stock in the state warehouses at all — the Studio
      // Platform Extender has 51 units there and zero in every state. Asking
      // this service about one returns "not available in WA", and in VIC, and
      // everywhere else, for something perfectly orderable. Their availability
      // is the shop-wide flag Liquid already renders them with.
      return [];
    }

    /** Walks a step's controls and takes out whatever this warehouse cannot
     *  ship. The markup already has the shape for it — `is-unavailable`, a
     *  disabled input and a note element — so this only flips what is there. */
    async applyStepStock(step) {
      if (!this.stockReady) return;
      var ids = this.variantsForStep(step);
      // The review has no options of its own, but it is where the whole build
      // is committed to, so it still re-audits what was chosen.
      if (!ids.length) {
        this.updateStockNotice();
        return;
      }

      var locationId = await this.resolveLocationId();
      if (!locationId) return;

      var stock = await this.stockForMany(ids, locationId);
      // The step may have moved on while the lookups were in flight.
      if (this.currentStep !== step) return;

      var self = this;
      var where = this.locationValue() || this.state.zone || '';

      this.querySelectorAll('[data-rf-control]').forEach(function (input) {
        var name = input.getAttribute('data-rf-control');
        if (name !== 'upholstery' && name !== 'propKit') return;

        var answer = stock[String(input.value)];
        if (!answer) return; // not known — leave it exactly as the server drew it

        var blocked = !answer.available || answer.comingSoon;
        if (!blocked && !input.hasAttribute('data-rf-stock-blocked')) return;

        self.markStock(input, blocked, answer, where);
      });

      // A frame whose colourways this warehouse cannot ship goes unavailable.
      this.syncFrame();
      this.updateStockNotice();
    }

    /** Marks one control, and remembers that it was this check that did it so
     *  a later check can put it back. Options the server already disabled are
     *  never re-enabled here. */
    markStock(input, blocked, answer, where) {
      // The note always sits inside the row, so it is found by walking there
      // rather than by id — an id selector would need escaping, and
      // CSS.escape is not somewhere we should need to rely on.
      var row = input.closest('.rf-option, .rf-swatch');
      var note = row ? row.querySelector('.rf-option__note, .rf-swatch__stock') : null;

      if (blocked) {
        if (!input.hasAttribute('data-rf-stock-blocked')) {
          input.setAttribute('data-rf-stock-blocked', '');
          input.setAttribute('data-rf-note-was', note ? note.textContent.trim() : '');
        }
        input.disabled = true;
        if (row) row.classList.add('is-unavailable');
        if (note) {
          note.textContent = answer.comingSoon
            ? this.config.copy.comingSoonNote || 'Coming soon'
            : (this.config.copy.outOfStockNote || 'Not available in {location}').replace('{location}', where);
        }
        return;
      }

      // Available again (the postcode changed): undo only our own marking.
      if (input.hasAttribute('data-rf-stock-blocked')) {
        input.removeAttribute('data-rf-stock-blocked');
        input.disabled = false;
        if (row) row.classList.remove('is-unavailable');
        if (note) {
          var was = input.getAttribute('data-rf-note-was');
          if (was) note.textContent = answer.note || was;
        }
        input.removeAttribute('data-rf-note-was');
      }
    }

    /** Everything the build is actually committing to, so a shared link can be
     *  audited against the postcode of whoever opened it. */
    selectedVariantIds() {
      // The Reformer and the prop kit, which is all this service speaks for.
      // The extras are stocked in the accessory warehouse rather than the
      // state ones, so a per-warehouse answer about them is always no.
      var ids = [];
      var uph = this.findUpholstery(this.state.upholstery);
      if (uph) ids.push(String(uph.variantId));
      if (this.state.propKit) ids.push(String(this.state.propKit));
      return ids;
    }

    /** The selections this warehouse cannot ship, named so the message can say
     *  which ones. Returns [] when nothing is wrong or nothing is known. */
    async auditSelection() {
      var self = this;
      var ids = this.selectedVariantIds();

      // Sold out everywhere: the server already drew these disabled, and no
      // warehouse answer is needed to know they cannot ship. A page swatch is
      // how one of them ends up chosen.
      var problems = ids
        .filter(function (id) {
          var option = self.findUpholstery(id) || self.findPropKit(id);
          return option && option.available === false;
        })
        .map(function (id) {
          return { variantId: id, label: self.labelForVariant(id) };
        });

      // Inclusion products are judged on the shop-wide flag only, like extras:
      // a gift can ship from the accessory warehouse, which the per-state
      // service would report as unavailable everywhere.
      (this.config.inclusionProducts || []).forEach(function (entry) {
        var option = self.resolveInclusionProduct(entry);
        if (option && option.available === false) {
          problems.push({ variantId: String(option.variantId), label: entry.title + ' · ' + option.label });
        }
      });

      if (!this.stockReady) return problems;
      var locationId = await this.resolveLocationId();
      if (!locationId) return problems;

      var stock = await this.stockForMany(ids, locationId);

      ids.forEach(function (id) {
        var answer = stock[id];
        if (!answer) return;
        if (answer.available && !answer.comingSoon) return;
        if (problems.some(function (p) { return p.variantId === id; })) return;
        problems.push({ variantId: id, label: self.labelForVariant(id) });
      });

      return problems;
    }

    labelForVariant(variantId) {
      var id = String(variantId);

      var uph = (this.config.upholstery || []).filter(function (o) {
        return String(o.variantId) === id;
      })[0];
      if (uph) return uph.label;

      var prop = ((this.config.propKit && this.config.propKit.options) || []).filter(function (o) {
        return String(o.variantId) === id;
      })[0];
      if (prop) return (this.config.propKit.title || 'Prop kit') + ' · ' + prop.label;

      var found = '';
      (this.config.inclusionProducts || []).forEach(function (e) {
        (e.options || []).forEach(function (o) {
          if (String(o.variantId) === id) found = e.title + ' · ' + o.label;
        });
      });
      (this.config.extras || []).forEach(function (e) {
        (e.variants || []).forEach(function (v) {
          if (String(v.variantId) === id) found = e.title + ' · ' + v.label;
        });
      });
      return found || 'One of your choices';
    }

    /** The standing notice in the card. Shown whenever something chosen cannot
     *  be shipped to the confirmed postcode — which is the state a shared
     *  build lands in when the person opening it lives somewhere else. */
    async updateStockNotice() {
      if (!this.stockNotice) return;
      var problems = await this.auditSelection();
      this.stockProblems = problems;

      // Nothing unshippable can be added: the button stands down for as long
      // as the notice is up, rather than only refusing once it is pressed.
      if (this.addBtn) this.addBtn.setAttribute('aria-disabled', problems.length ? 'true' : 'false');

      if (!problems.length) {
        this.stockNotice.hidden = true;
        this.stockNotice.textContent = '';
        return;
      }

      var names = problems.map(function (p) { return p.label; }).join(', ');
      var message = (this.config.copy.stockBlocked ||
        'Not available for delivery to {postcode}. Choose another option to continue.')
        .replace('{postcode}', this.state.postcode || '')
        .replace('{items}', names);

      this.stockNotice.textContent = names + ' — ' + message;
      this.stockNotice.hidden = false;
    }

    /* ---- the URL -------------------------------------------------------
       The build is addressable: every step and every choice is written into
       the query string, so a link opens the flow where it left off and a
       finished build can be handed to someone else.

       What travels is the build — upholstery, prop kit, payment, extras and
       which step is up. What does not travel is the postcode. It derives the
       zone, the warehouse, the lead time and the cart's location attribute,
       so a link carrying the sender's postcode would quietly promise the
       recipient a delivery that was never checked for them. Whoever opens the
       link supplies their own, and if this browser already has one confirmed
       they are not asked again.

       Written with replaceState: the URL always describes what is on screen,
       and the browser's own Back still means "leave this page" rather than
       unwinding the build one step at a time. */

    get urlKeys() {
      return {
        step: 'rf',
        upholstery: 'rf-uph',
        propKit: 'rf-prop',
        payment: 'rf-pay',
        extras: 'rf-extra',
        inclusions: 'rf-inc'
      };
    }

    /** Reads a build out of the query string. Everything is checked against
     *  the config before it is believed — a link can be edited by hand, can
     *  outlive a merchandising change, and must never be able to put the flow
     *  into a state its own markup cannot render. */
    readUrlState() {
      var params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        return null;
      }

      var keys = this.urlKeys;
      var step = params.get(keys.step);
      if (!step || this.steps.indexOf(step) === -1) return null;

      var out = { step: step, upholstery: null, propKit: null, payment: null, extras: {}, inclusionPicks: {} };

      var uph = params.get(keys.upholstery);
      if (uph && this.findUpholstery(uph)) out.upholstery = uph;

      var prop = params.get(keys.propKit);
      if (prop && this.findPropKit(prop)) out.propKit = prop;

      var pay = params.get(keys.payment);
      if (pay && this.findPayment(pay)) out.payment = pay;

      // "productId:variantId,productId:variantId" — a pair is kept only if
      // both halves still exist and belong together.
      var extras = params.get(keys.extras);
      if (extras) {
        var self = this;
        extras.split(',').forEach(function (pair) {
          var bits = pair.split(':');
          if (bits.length !== 2) return;
          var productId = bits[0];
          var variantId = bits[1];
          var extra = (self.config.extras || []).filter(function (e) {
            return String(e.productId) === String(productId);
          })[0];
          if (!extra) return;
          var variant = (extra.variants || []).filter(function (v) {
            return String(v.variantId) === String(variantId);
          })[0];
          if (!variant) return;
          out.extras[productId] = variantId;
        });
      }

      // "inclusionId:variantId,…" for inclusion steps, held to the same rule:
      // the pair is kept only if the step exists and offers that variant.
      var incs = params.get(keys.inclusions);
      if (incs) {
        var me = this;
        incs.split(',').forEach(function (pair) {
          var bits = pair.split(':');
          if (bits.length !== 2) return;
          var entry = me.findInclusionProduct(bits[0]);
          if (!entry || !entry.hasStep) return;
          var known = (entry.options || []).some(function (o) {
            return String(o.variantId) === String(bits[1]);
          });
          if (known) out.inclusionPicks[entry.id] = bits[1];
        });
      }

      return out;
    }

    /** A link is an instruction, so it wins over whatever the session was
     *  holding — but only for what it actually names. */
    applyUrlState(url) {
      if (!url) return;

      if (url.upholstery) this.state.upholstery = url.upholstery;
      if (url.propKit) this.state.propKit = url.propKit;
      if (url.payment) this.state.payment = url.payment;

      // Extras are replaced wholesale rather than merged: a shared build is a
      // complete list, and an extra the sender removed must not come back
      // from the recipient's own session.
      this.state.extras = url.extras;
      this.state.inclusionPicks = url.inclusionPicks || {};
      this.explicitExtraPicks = {};
      Object.keys(url.extras).forEach(
        function (productId) {
          this.explicitExtraPicks[productId] = true;
        }.bind(this)
      );

      var index = this.steps.indexOf(url.step);
      this.state.stepIndex = index === -1 ? 0 : index;
      this.deepLinkStep = url.step;
      this.persist();
    }

    /** Writes the current build back into the address bar. Called whenever a
     *  choice or a step changes, and on close — where the parameters are
     *  stripped, because with the card shut there is nothing for them to
     *  describe. Any other query string on the page (preview_theme_id, utm,
     *  the variant) is left exactly as it was. */
    syncUrl() {
      if (!window.history || !window.history.replaceState) return;

      var url;
      try {
        url = new URL(window.location.href);
      } catch (e) {
        return;
      }

      var keys = this.urlKeys;
      Object.keys(keys).forEach(function (k) {
        url.searchParams.delete(keys[k]);
      });

      if (this.buildPanel && this.buildPanel.isOpen) {
        url.searchParams.set(keys.step, this.currentStep);
        if (this.state.upholstery) url.searchParams.set(keys.upholstery, this.state.upholstery);
        if (this.state.propKit) url.searchParams.set(keys.propKit, this.state.propKit);
        if (this.state.payment) url.searchParams.set(keys.payment, this.state.payment);

        var extras = Object.keys(this.state.extras || {}).map(
          function (productId) {
            return productId + ':' + this.state.extras[productId];
          }.bind(this)
        );
        if (extras.length) url.searchParams.set(keys.extras, extras.join(','));

        var incs = Object.keys(this.state.inclusionPicks || {}).map(
          function (id) {
            return id + ':' + this.state.inclusionPicks[id];
          }.bind(this)
        );
        if (incs.length) url.searchParams.set(keys.inclusions, incs.join(','));
      }

      var next = url.pathname + (url.search ? url.search : '') + url.hash;
      try {
        window.history.replaceState(window.history.state, '', next);
      } catch (e) {
        /* some embedded webviews refuse replaceState; the flow is unaffected */
      }
    }

    /** Hands over the link in the address bar. It is already current — every
     *  choice and every step rewrites it — so there is nothing to build here,
     *  only to copy. */
    copyBuildLink() {
      var self = this;
      var label = this.shareBtn && this.shareBtn.querySelector('[data-rf-share-label]');
      var original = this.config.copy.shareLabel || 'Save for later';

      var say = function (message) {
        if (label) label.textContent = message;
        self.announce(message);
        window.clearTimeout(self.shareTimer);
        self.shareTimer = window.setTimeout(function () {
          if (label) label.textContent = original;
        }, 2400);
      };

      try {
        navigator.clipboard.writeText(window.location.href).then(
          function () {
            say(self.config.copy.shareDone || 'Link copied');
          },
          function () {
            say(self.config.copy.shareFailed || 'Could not copy the link');
          }
        );
      } catch (e) {
        say(this.config.copy.shareFailed || 'Could not copy the link');
      }
    }

    /** Opening from a link. The step is already set; what decides the route is
     *  whether we know where this person lives. */
    openFromUrl() {
      if (!this.deepLinkStep) return;
      if (this.state.serviceable === true && this.state.postcode) {
        this.startBuild(this.trigger);
      } else {
        // The postcode is asked first and the build opens behind it — the
        // shared step is already loaded, so confirming lands straight on it.
        this.openPostcode(this.trigger);
      }
      this.deepLinkStep = null;
    }

    /* ---- navigation ---- */

    get currentStep() {
      return this.steps[this.state.stepIndex];
    }

    goToStep(name, options) {
      options = options || {};
      var index = this.steps.indexOf(name);
      if (index === -1) index = 0;
      this.state.stepIndex = index;
      this.returnToSummary = !!options.returnToSummary;
      this.persist();
      this.renderStep();
    }

    goNext() {
      // Confirm, while the colourway picker is up. The colour is already
      // applied — the radio wrote it on change — so this only closes the view.
      if (this.extrasPickerProduct) {
        this.closeExtrasPicker();
        return;
      }
      if (!this.validateStep()) return;
      if (this.returnToSummary) {
        this.returnToSummary = false;
        this.goToStep('summary');
        return;
      }
      if (this.state.stepIndex < this.steps.length - 1) {
        this.state.stepIndex += 1;
        this.persist();
        this.renderStep();
      }
    }

    goBack() {
      // Back out of the picker to the list it came from, not out of the step.
      if (this.extrasPickerProduct) {
        this.closeExtrasPicker();
        return;
      }
      // Back from the first step leaves the build for the product page; the
      // postcode panel is only reopened from "Ships to XXXX" (SHOP-161).
      if (this.state.stepIndex === 0) {
        this.buildPanel.close('back');
        return;
      }
      this.state.stepIndex -= 1;
      this.persist();
      this.renderStep();
    }

    /** A step can only be left once its question has an answer. Errors are
     *  helper text under the group, never a colour-only signal. */
    validateStep() {
      var step = this.currentStep;
      var node = this.stepNodes[step];
      if (!node) return true;
      var error = node.querySelector('[data-rf-step-error]');
      if (error) error.hidden = true;

      var required = node.getAttribute('data-rf-required');
      if (!required) return true;
      if (required.indexOf('inclusion:') === 0) {
        if ((this.state.inclusionPicks || {})[required.slice('inclusion:'.length)]) return true;
      } else if (this.state[required]) {
        return true;
      }

      if (error) {
        error.hidden = false;
        error.focus && error.focus();
      }
      this.announce(error ? error.textContent : 'Make a selection to continue.');
      var firstInput = node.querySelector('input:not(:disabled)');
      if (firstInput) firstInput.focus();
      return false;
    }

    renderStep() {
      var self = this;
      var step = this.currentStep;

      // The picker belongs to the extras step. Arriving anywhere else — or
      // back at extras from somewhere else — starts on the list.
      if (this.extrasPickerProduct) {
        this.extrasPickerProduct = null;
        this.extrasPickerOpener = null;
        if (this.extrasPickerView) this.extrasPickerView.hidden = true;
        if (this.extrasList) this.extrasList.hidden = false;
      }

      Object.keys(this.stepNodes).forEach(function (name) {
        self.stepNodes[name].hidden = name !== step;
      });

      var isSummary = step === 'summary';
      var number = this.steps.indexOf(step) + 1;

      // Progress is an indicator, not a control.
      Array.prototype.forEach.call(this.progressSegments, function (seg, i) {
        seg.classList.toggle('is-complete', i < number - 1 || isSummary);
        seg.classList.toggle('is-current', !isSummary && i === number - 1);
      });

      // The summary is not a numbered step, and the board draws no progress
      // indicator on it — the build is done being built.
      if (this.progressCount) {
        this.progressCount.textContent = isSummary ? '' : number + ' / ' + this.buildStepCount;
        this.progressCount.hidden = isSummary;
      }
      if (this.progressTrack) this.progressTrack.hidden = isSummary;
      if (this.summaryTotals) this.summaryTotals.hidden = !isSummary;

      var heading = this.querySelector('[data-rf-panel-heading]');
      if (heading) {
        heading.textContent = isSummary
          ? this.config.copy.summaryTitle || 'Your Reformer Build'
          : (this.config.copy.buildTitle || 'Build your Reformer');
      }

      if (this.nextBtn) this.nextBtn.hidden = isSummary;
      if (this.addBtn) this.addBtn.hidden = !isSummary;
      var runningTotal = this.querySelector('.rf-foot__total');
      if (runningTotal) runningTotal.hidden = isSummary;
      // updateTotals() runs after this and has the final say, because whether
      // the running total belongs on screen depends on the payment choice.
      if (this.backBtn) this.backBtn.hidden = false;

      if (this.nextBtn && !isSummary) {
        var nextIsSummary = this.steps[number] === 'summary';
        this.nextBtn.querySelector('[data-rf-next-label]').textContent = nextIsSummary
          ? this.config.copy.reviewLabel || 'Review Total'
          : this.config.copy.nextLabel || 'Next';
      }

      this.syncExtrasFooter();
      this.syncUrl();

      // Stock is per warehouse, so what this step can offer depends on the
      // postcode. Runs in the background; the step is already drawn.
      var stockChecked = this.applyStepStock(step);
      // The colour-matched kit is only preselected once this warehouse's stock
      // is known, so a kit that cannot ship is never ticked for them.
      if (step === 'props') {
        stockChecked.then(function () {
          if (self.currentStep !== 'props' || self.state.propKitPicked) return;
          var before = self.state.propKit;
          self.syncDefaultPropKit();
          if (self.state.propKit !== before) {
            self.renderScene();
            self.updateTotals();
            self.syncUrl();
          }
        });
      }

      // The summary is the one screen that earns a sticky footer on mobile.
      if (this.buildPanel) {
        var panelEl = this.buildPanel.querySelector('.rf-panel');
        panelEl.classList.toggle('rf-panel--sticky-foot', isSummary);
        panelEl.classList.toggle('rf-panel--summary', isSummary);
      }

      // The stage reads the current step so it can give up height on the
      // tallest cards (see the mobile rules in reformer-flow.css).
      if (this.stageHost) this.stageHost.setAttribute('data-rf-step', step);
      // On mobile the section is the scroller; each step starts at its top
      // rather than wherever the last one was left, under the pinned header.
      if (this.sectionEl) this.sectionEl.scrollTop = 0;

      // Re-composite: which layers belong in the scene depends on how far the
      // build has got, not only on what is selected.
      this.renderScene();

      if (isSummary) this.renderSummary();
      this.updateTotals();

      // Move focus to the step heading so the change is unmistakable, and
      // announce it for anyone not watching focus.
      var stepNode = this.stepNodes[step];
      var stepHeading = stepNode && stepNode.querySelector('[data-rf-step-heading]');
      if (stepHeading) {
        if (!stepHeading.hasAttribute('tabindex')) stepHeading.setAttribute('tabindex', '-1');
        window.requestAnimationFrame(
          function () {
            // The step may have moved on, or the card closed, before the frame
            // landed; either way focus belongs wherever it is now.
            if (stepHeading.closest('[hidden]')) return;
            if (this.buildPanel && !this.buildPanel.isOpen) return;
            try {
              stepHeading.focus({ preventScroll: true });
            } catch (e) {
              stepHeading.focus();
            }
          }.bind(this)
        );
        this.announce(
          (isSummary ? '' : 'Step ' + number + ' of ' + this.buildStepCount + '. ') + stepHeading.textContent
        );
      }

      if (this.buildPanel && this.buildPanel.updateScrollShadow) this.buildPanel.updateScrollShadow();

      // Steps are different heights, and the section's sticky script sizes the
      // column from the buy box wrapper. Nudge it so the sticky range always
      // matches the card that is actually on screen.
      window.dispatchEvent(new Event('resize'));
    }

    /* ---- money ---- */

    money(cents) {
      return formatMoney(cents, this.config.moneyFormat);
    }

    totalCents() {
      var total = 0;
      var uph = this.findUpholstery(this.state.upholstery);
      if (uph) total += uph.price;

      var self = this;
      Object.keys(this.state.extras).forEach(function (productId) {
        var extra = (self.config.extras || []).filter(function (e) {
          return String(e.productId) === String(productId);
        })[0];
        if (!extra) return;
        var variantId = self.state.extras[productId];
        var variant = (extra.variants || []).filter(function (v) {
          return String(v.variantId) === String(variantId);
        })[0];
        total += variant ? variant.price : extra.price;
      });

      // Only an as_bundled_product line is zeroed by the bundle discount; any
      // other inclusion product is charged, as it always was from the buy box.
      (this.config.inclusionProducts || []).forEach(function (entry) {
        if (entry.bundled) return;
        var option = self.resolveInclusionProduct(entry);
        if (option && option.price) total += option.price;
      });

      return total;
    }

    /** What the build saves, line by line, as the cart drawer's Promotions
     *  line will count it once it is in the cart. Each line's figure is worked
     *  out in Liquid (snippets/reformer-flow-saving.liquid); the payment plan
     *  is not a saving, so the plan never changes it. Visual-only inclusions
     *  never reach the cart, so the drawer never counts them either. */
    savingCents() {
      var self = this;
      var saving = 0;
      var uph = this.findUpholstery(this.state.upholstery);
      if (uph) saving += uph.saving || 0;
      var prop = this.findPropKit(this.state.propKit);
      if (prop) saving += prop.saving || 0;
      (this.config.inclusions || []).forEach(function (inc) {
        if (inc.visualOnly) return;
        var resolved = self.resolveInclusion(inc);
        if (resolved) saving += resolved.saving || 0;
      });
      (this.config.inclusionProducts || []).forEach(function (entry) {
        var option = self.resolveInclusionProduct(entry);
        if (option) saving += option.saving || 0;
      });
      Object.keys(this.state.extras).forEach(function (productId) {
        var extra = (self.config.extras || []).filter(function (e) {
          return String(e.productId) === String(productId);
        })[0];
        if (!extra) return;
        var variant = (extra.variants || []).filter(function (v) {
          return String(v.variantId) === String(self.state.extras[productId]);
        })[0];
        if (variant) saving += variant.saving || 0;
      });
      return saving;
    }

    /** Week -> wk, Month -> mo, Fortnight -> ftnt, matching the abbreviations
     *  the PDP's own plan display uses. */
    periodAbbrev(range) {
      var map = { week: 'wk', month: 'mo', fortnight: 'ftnt' };
      var key = String(range || '').toLowerCase();
      return map[key] || key;
    }

    /** With a plan chosen the price is two numbers, not one, so it is shown
     *  as two: deposit and per-period either side of a rule, with the
     *  outright alternative beneath. Mirrors the buy box on the PDP. */
    /** Plan pricing shows once the payment is a real choice: the payment step
     *  has been reached, or a plan was picked (step or PDP pills), so a
     *  default plan never shows plan pricing to someone who has not chosen. */
    planChosen() {
      // US financing splits are set per customer by Affirm, so the store turns
      // the deposit/per-period display off altogether (SHOP-160).
      if (this.config.hidePlanBreakdown) return false;
      var paymentStep = this.steps.indexOf('payment');
      return paymentStep === -1 || this.state.stepIndex >= paymentStep || !!this.state.paymentPicked;
    }

    renderPlanPrice() {
      if (!this.planPrice) return false;

      var plan = this.findPayment(this.state.payment);
      var show = !!(plan && plan.isPlan && plan.perPayment && this.planChosen());

      this.planPrice.hidden = !show;
      if (!show) return false;

      this.planDeposit.textContent = this.money(plan.deposit);


      if (this.planWas) {
        var hasWas = plan.depositCompare > plan.deposit;
        this.planWas.hidden = !hasWas;
        if (hasWas) this.planWas.textContent = this.money(plan.depositCompare);
      }

      var abbrev = this.periodAbbrev(plan.range);
      this.planPer.innerHTML = '';
      this.planPer.appendChild(document.createTextNode(this.money(plan.perPayment)));
      if (abbrev) {
        var unit = document.createElement('span');
        unit.className = 'rf-planprice__unit';
        unit.textContent = ' /' + abbrev;
        this.planPer.appendChild(unit);
      }

      // "9 month plan". The outright price is not repeated here — the option
      // list above already carries it.
      this.planTerm.textContent = [plan.periods, String(plan.range || '').toLowerCase(), 'plan']
        .filter(Boolean)
        .join(' ');

      return true;
    }

    /** The buy box price, kept in step with the payment choice.
     *
     *  Same two states the pill toggle had: the price snippet's own number at
     *  display size, or the plan's deposit and per-period amounts either side
     *  of a rule. The number for the outright state is never written here —
     *  price.liquid already printed it, promoline strikethrough and all, and
     *  reprinting it from the config would be a second pricing implementation
     *  that could disagree with the first.
     *
     *  Called when the build closes and on load, not on every change: while
     *  the build is open the buy box has stood down and none of this is on
     *  screen. The same gate as the card's own split display is used, so a
     *  default plan cannot show plan pricing to someone who has never opened
     *  the flow.
     */
    syncBuyBoxPrice() {
      this.syncPlanPills();
      if (!this.priceHost) return;

      var plan = this.findPayment(this.state.payment);
      var onPlan = !!(plan && plan.isPlan && plan.perPayment && this.planChosen());

      // Both the price row and the supplement below it key off the same
      // attribute, so there is no state in which each shows a number.
      var mode = onPlan ? 'plan' : 'outright';
      this.priceHost.setAttribute('data-rf-price-mode', mode);
      if (this.priceSupplement) this.priceSupplement.setAttribute('data-rf-price-mode', mode);
      if (!onPlan) return;

      if (this.priceDeposit) this.priceDeposit.textContent = this.money(plan.deposit);

      if (this.priceWas) {
        var hasWas = plan.depositCompare > plan.deposit;
        this.priceWas.hidden = !hasWas;
        if (hasWas) this.priceWas.textContent = this.money(plan.depositCompare);
      }

      if (this.pricePer) {
        var abbrev = this.periodAbbrev(plan.range);
        this.pricePer.innerHTML = '';
        this.pricePer.appendChild(document.createTextNode(this.money(plan.perPayment)));
        if (abbrev) {
          var unit = document.createElement('span');
          unit.className = 'plan-price-unit';
          unit.textContent = ' /' + abbrev;
          this.pricePer.appendChild(unit);
        }
      }

      if (this.priceTerm) {
        this.priceTerm.textContent = [plan.periods, String(plan.range || '').toLowerCase(), 'plan']
          .filter(Boolean)
          .join(' ');
      }
    }

    updateTotals() {
      if (!this.footTotal) return;
      var total = this.totalCents();
      this.footTotal.textContent = this.money(total);

      // The split display replaces the single running total when it is up.
      var onPlan = this.renderPlanPrice();
      var runningTotal = this.querySelector('.rf-foot__total');
      if (runningTotal && this.currentStep !== 'summary') runningTotal.hidden = onPlan;
      // The summary's totals say the plan themselves (SHOP-166).
      if (this.planPrice && this.currentStep === 'summary') this.planPrice.hidden = true;


      var meta = [];
      var step = this.currentStep;

      // The badge belongs to the product the step is choosing, so it only
      // shows on a bundled product's own step: the jumpboard and box on the
      // upholstery step, which picks its colour (or the frame step when that
      // is the whole choice), the prop kit (its own badge copy, SHOP-156) and
      // an inclusion product with a step. Nowhere else.
      var footSaving = this.querySelector('[data-rf-foot-saving]');
      if (footSaving) {
        var badgeSaving = 0;
        var badgeText = '';
        var picksColour = step === 'upholstery' || (step === 'frame' && this.steps.indexOf('upholstery') === -1);
        if (picksColour) {
          var jumpboard = (this.config.inclusions || []).filter(function (inc) {
            return inc.key === 'jumpboard';
          })[0];
          badgeSaving = jumpboard ? (this.resolveInclusion(jumpboard) || {}).saving || 0 : 0;
        } else if (step === 'props') {
          badgeText = this.config.copy.propKitBadge || '';
          badgeSaving = (this.findPropKit(this.state.propKit) || {}).saving || 0;
        } else if (step && step.indexOf('inc-') === 0) {
          var badgeEntry = this.findInclusionProduct(step.slice(4));
          badgeSaving = (this.resolveInclusionProduct(badgeEntry) || {}).saving || 0;
        }
        if (!badgeText && badgeSaving) badgeText = 'Save ' + this.money(badgeSaving).replace(/[.,]00(?!\d)/, '');
        footSaving.hidden = !badgeText;
        footSaving.textContent = badgeText;
      }

      if ((step === 'frame' || step === 'upholstery') && this.state.postcode) {
        meta.push('Ships to ' + this.state.postcode);
      } else if (step === 'props') {
        var uph = this.findUpholstery(this.state.upholstery);
        var prop = this.findPropKit(this.state.propKit);
        if (uph) meta.push(uph.label);
        if (prop) meta.push(prop.label + ' kit');
      } else if (step && step.indexOf('inc-') === 0) {
        var incEntry = this.findInclusionProduct(step.slice(4));
        var incOption = this.resolveInclusionProduct(incEntry);
        if (incEntry) meta.push(incOption ? incEntry.title + ' · ' + incOption.label : incEntry.title);
        meta.push('included');
      } else if (step === 'payment') {
        var plan = this.findPayment(this.state.payment);
        if (plan) meta.push(plan.summaryLabel || plan.label);
      } else if (step === 'extras' || step === 'summary') {
        var count = Object.keys(this.state.extras).length;
        if (count) {
          // No saving beside it: extras will not be a bundle (SHOP-157).
          meta.push(count + (count === 1 ? ' extra added' : ' extras added'));
        } else if (this.state.postcode) {
          meta.push('Delivering to ' + this.state.postcode);
        }
      }

      if (this.footMeta) this.footMeta.textContent = meta.join(' · ');
    }

    /* ---- summary -------------------------------------------------------
       Two line types. Chosen lines carry a swatch and an Edit link;
       inclusions are indented with a value label instead. Both read as grey
       text — no coloured badges. */

    renderSummary() {
      if (!this.summaryList) return;
      var self = this;
      var rows = [];
      // A line's price, with what it would have cost struck through above it
      // (SHOP-166). The strikethrough is the price plus the line's saving, the
      // same saving savingCents() totals, so the Savings line always adds up.
      var free = this.config.copy.freeLabel || 'Free';
      function priced(row, cents, saving) {
        row.valueLabel = cents ? self.money(cents) : free;
        row.isFree = !cents;
        row.wasLabel = saving ? self.money(cents + saving) : '';
        return row;
      }

      // In the order the customer thinks of it (SHOP-159): the Reformer by
      // its model, what comes with it, the prop kit, then the extras.
      var uph = this.findUpholstery(this.state.upholstery);
      var uphSwatch = uph ? uph.swatchImage || uph.swatchColor : null;
      if (uph) {
        rows.push(priced({
          type: 'choice',
          // Edit goes to the colourways, or to the frame when that is the
          // only choice.
          step: this.steps.indexOf('upholstery') === -1 && this.steps.indexOf('frame') !== -1 ? 'frame' : 'upholstery',
          // "YR Original Maple": the model and its frame.
          label: [this.config.productTitle, uph.frame].filter(Boolean).join(' ') || 'Upholstery',
          value: uph.label,
          swatch: uphSwatch
        }, uph.price, uph.saving || 0));
      }

      (this.config.inclusions || []).forEach(function (inc) {
        var resolved = self.resolveInclusion(inc);
        // The jumpboard and box follow the upholstery, so they wear its swatch.
        var incSwatch = inc.colourMatched ? uphSwatch : null;
        var incRow = {
          type: incSwatch ? 'choice' : 'inclusion',
          swatch: incSwatch,
          label: inc.title,
          value: resolved && resolved.label ? resolved.label : inc.detail || '',
          valueLabel: inc.valueLabel || 'Free',
          wasLabel: inc.wasLabel || ''
        };
        // Bundled free. A visual-only line keeps its badge's worth: it is not
        // in the cart, so it is not in Savings either.
        var incSaving = !inc.visualOnly && resolved ? resolved.saving || 0 : 0;
        rows.push(incSaving ? priced(incRow, 0, incSaving) : incRow);
      });

      var prop = this.findPropKit(this.state.propKit);
      if (prop) {
        // Bundled free, so its saving is its whole price.
        rows.push(priced({
          type: 'choice',
          step: 'props',
          label: this.config.propKit.title || 'Prop Starter Kit',
          value: prop.label,
          swatch: prop.swatchImage || prop.swatchColor
        }, 0, prop.saving || 0));
      }

      (this.config.inclusionProducts || []).forEach(function (entry) {
        var option = self.resolveInclusionProduct(entry);
        var stepped = entry.hasStep && entry.options && entry.options.length > 1;
        var ipRow = {
          type: 'inclusion',
          step: stepped ? 'inc-' + entry.id : null,
          label: entry.title,
          value: option && entry.options.length > 1 ? option.label : '',
          valueLabel: entry.bundled ? 'Included' : option ? self.money(option.price) : ''
        };
        rows.push(option && option.saving ? priced(ipRow, entry.bundled ? 0 : option.price, option.saving) : ipRow);
      });

      Object.keys(this.state.extras).forEach(function (productId) {
        var extra = (self.config.extras || []).filter(function (e) {
          return String(e.productId) === String(productId);
        })[0];
        if (!extra) return;
        var variantId = self.state.extras[productId];
        var variant = (extra.variants || []).filter(function (v) {
          return String(v.variantId) === String(variantId);
        })[0];
        // Extras are removed with the bin, not edited here (SHOP-159).
        rows.push(priced({
          type: 'choice',
          label: extra.title,
          value: variant && variant.label ? variant.label : '',
          swatch: variant ? variant.swatchImage || variant.swatchColor : null,
          removeProduct: productId
        }, variant ? variant.price : extra.price, variant ? variant.saving || 0 : 0));
      });

      this.summaryList.innerHTML = '';
      rows.forEach(function (row) {
        var li = document.createElement('li');
        li.className = 'rf-summary__row' + (row.type === 'inclusion' ? ' rf-summary__row--inclusion' : '');

        if (row.swatch) {
          var sw = document.createElement('span');
          sw.className = 'rf-summary__swatch';
          if (/^https?:|^\/\//.test(row.swatch)) {
            sw.style.backgroundImage = 'url(' + row.swatch + ')';
          } else {
            sw.style.backgroundColor = row.swatch;
          }
          li.appendChild(sw);
        }

        var text = document.createElement('span');
        text.className = 'rf-summary__text';
        text.textContent = row.value ? row.label + ' · ' + row.value : row.label;
        li.appendChild(text);

        if (row.step) {
          var edit = document.createElement('button');
          edit.type = 'button';
          edit.className = 'rf-summary__edit';
          edit.setAttribute('data-rf-edit', row.step);
          edit.textContent = 'Edit';
          // Screen readers get the full context, not a wall of "Edit".
          var sr = document.createElement('span');
          sr.className = 'rf-sr-only';
          sr.textContent = ' ' + row.label + (row.value ? ', currently ' + row.value : '');
          edit.appendChild(sr);
          li.appendChild(edit);
        }

        // Edit sits before the price, which stacks under its strikethrough.
        if (row.valueLabel) {
          var val = document.createElement('span');
          val.className = 'rf-summary__value';
          if (row.wasLabel) {
            var was = document.createElement('s');
            was.className = 'rf-summary__was';
            was.textContent = row.wasLabel;
            val.appendChild(was);
            val.appendChild(document.createTextNode(' '));
          }
          var now = document.createElement('span');
          now.className = 'rf-summary__now' + (row.isFree ? ' rf-summary__now--free' : '');
          now.textContent = row.valueLabel;
          val.appendChild(now);
          li.appendChild(val);
        }

        // Extras are optional, so they can be dropped right here (SHOP-147).
        if (row.removeProduct) {
          var remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'rf-summary__remove';
          remove.setAttribute('data-rf-remove', row.removeProduct);
          remove.setAttribute('aria-label', 'Remove ' + row.label);
          remove.innerHTML =
            '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">' +
            '<path d="M2.5 4h11M6 4V2.5h4V4M4 4l.7 9.5h6.6L12 4M6.75 6.5v4.5M9.25 6.5v4.5" ' +
            'stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          li.appendChild(remove);
        }

        self.summaryList.appendChild(li);
      });

      // "Total, paid outright" over "Delivering to 3220 · free" — the shipping
      // promise, not the lead time, which the validated panel already gave.
      var deliveryLine = this.querySelector('[data-rf-summary-delivery]');
      if (deliveryLine && this.state.postcode) {
        deliveryLine.textContent = 'Delivering to ' + this.state.postcode;
      }

      // On a plan, Total is what is paid today: the deposit, plus the add-ons,
      // which are not on the plan. The per-period amount goes under it.
      var plan = this.findPayment(this.state.payment);
      var onPlan = !!(plan && plan.isPlan && plan.perPayment && this.planChosen());
      var total = this.totalCents();
      if (onPlan && uph) total = plan.deposit + total - uph.price;
      if (this.summaryAmount) this.summaryAmount.textContent = this.money(total);
      var per = this.querySelector('[data-rf-summary-per]');
      if (per) {
        var abbrev = onPlan ? this.periodAbbrev(plan.range) : '';
        per.hidden = !onPlan;
        // "$53.50 /wk (39 weeks)"
        var count = parseInt(plan && plan.periods, 10);
        var range = String((plan && plan.range) || '').toLowerCase();
        var term = count && range ? ' (' + count + ' ' + range + (count === 1 ? '' : 's') + ')' : '';
        per.textContent = onPlan ? this.money(plan.perPayment) + (abbrev ? ' /' + abbrev : '') + term : '';
      }

      var savingRow = this.querySelector('[data-rf-summary-saving]');
      if (savingRow) {
        var saving = this.savingCents();
        savingRow.hidden = !saving;
        savingRow.querySelector('[data-rf-summary-saving-amount]').textContent = '- ' + this.money(saving);
      }

      var planLine = this.querySelector('[data-rf-summary-plan]');
      if (planLine) {
        // Verbatim: the label is merchandiser copy from the payment metaobject,
        // and lower-casing it turned "YR Payment Plan" into "yr payment plan".
        var planLabel = plan ? plan.summaryLabel || plan.label : '';
        // The plan on its own line above a plain Total (SHOP-159).
        planLine.textContent = this.config.copy.totalLabel || 'Total';
        var planName = this.querySelector('[data-rf-summary-plan-name]');
        var planRow = this.querySelector('[data-rf-summary-plan-row]');
        if (planName) planName.textContent = planLabel;
        if (planRow) planRow.hidden = !planLabel;
        var deposit = this.querySelector('[data-rf-summary-plan-deposit]');
        if (deposit) deposit.textContent = onPlan && plan.deposit ? this.money(plan.deposit) : '';
        var depositNote = this.querySelector('[data-rf-summary-plan-note]');
        if (depositNote) depositNote.hidden = !(onPlan && plan.deposit);
      }
    }

    /* ---- add to cart ---------------------------------------------------
       Posts the Reformer, its inclusions and the accessories as one line-item
       group, so the cart shows a build rather than six products. The
       properties below match the contract product-form-pre-order.js already
       uses, so the cart drawer and checkout group them the same way. */

    /** options.checkout: go to checkout once added, rather than the drawer. */
    /* ---- PDP payment pills (SHOP-154) ----
       The section's pill toggle, rendered without its own script. A pill
       picks the build's payment exactly as the payment step does, and the
       pills always show the build's payment for the colour on the page. */

    planValue(name) {
      return String(name || '').toUpperCase().replace(/ /g, '_');
    }

    pickPlanFromPill(pill) {
      var value = this.planValue(pill.getAttribute('data-plan-name'));
      if (!this.findPayment(value)) return;
      if (pill.classList.contains('plan-sub-pill')) {
        pill.parentNode.querySelectorAll('.plan-sub-pill').forEach(function (sp) {
          sp.classList.toggle('plan-sub-pill--active', sp === pill);
        });
      }
      this.state.payment = value;
      this.state.paymentPicked = true;
      var radio = this.querySelector('[data-rf-control="payment"][value="' + value + '"]');
      if (radio) radio.checked = true;
      this.persist();
      this.updateTotals();
      this.syncBuyBoxPrice();
      this.syncUrl();
    }

    syncPlanPills() {
      if (!this.planPills) return;
      var self = this;
      var variant = String(this.pageVariant || this.state.upholstery);
      this.planPills.querySelectorAll('.pill-variant-plans').forEach(function (wrap) {
        wrap.classList.toggle('pill-variant-plans--active', wrap.getAttribute('data-variant-id') === variant);
        wrap.querySelectorAll('.pill-main-row .plan-pill').forEach(function (p) {
          p.classList.toggle('plan-pill--active', self.planValue(p.getAttribute('data-plan-name')) === self.state.payment);
        });
        wrap.querySelectorAll('.plan-sub-toggle').forEach(function (st) {
          st.classList.toggle('plan-sub-toggle--visible', self.planValue(st.getAttribute('data-group')) === self.state.payment);
        });
      });
    }

    /** Unticks the extra exactly as the extras step would, then redraws the
     *  summary and puts focus somewhere that still exists. */
    removeExtraFromSummary(btn) {
      var productId = btn.getAttribute('data-rf-remove');
      var li = btn.closest('li');
      var index = li ? Array.prototype.indexOf.call(this.summaryList.children, li) : -1;
      var label = (btn.getAttribute('aria-label') || '').replace(/^Remove /, '');
      var toggle = this.querySelector('[data-rf-control="extra"][data-product="' + productId + '"]');
      if (toggle) {
        toggle.checked = false;
        this.onControlChange('extra', toggle);
      } else {
        delete this.state.extras[productId];
        this.persist();
        this.updateTotals();
        this.syncUrl();
      }
      this.renderSummary();
      this.announce(label + ' removed.');
      var rows = this.summaryList.children;
      var next = rows[Math.min(index, rows.length - 1)];
      this.focusSoon((next && next.querySelector('button')) || this.addBtn);
    }

    async addToCart(options) {
      // Only an add already in flight is ignored. A button stood down for stock
      // still answers a tap, with the refusal below naming what cannot ship.
      if (this.addBtn.getAttribute('aria-busy') === 'true') return;

      if (!this.state.postcode || this.state.serviceable !== true) {
        this.showCartError(this.config.copy.needPostcode || 'Confirm your delivery postcode first.');
        return;
      }

      var uph = this.findUpholstery(this.state.upholstery);
      if (!uph) {
        this.showCartError(this.config.copy.needUpholstery || 'Choose an upholstery colour first.');
        return;
      }

      // The last gate. Everything chosen is re-checked against the warehouse
      // the postcode resolves to, because the postcode can have changed since
      // the choice was made — which is exactly what happens when a shared
      // build is opened by someone in another state. Cached, so this is
      // normally free; a lookup that cannot answer does not block the sale.
      var blocked = await this.auditSelection();
      if (blocked.length) {
        var names = blocked.map(function (p) { return p.label; }).join(', ');
        this.showCartError(
          names + ' — ' +
          (this.config.copy.stockBlocked ||
            'Not available for delivery to {postcode}. Choose another option to continue.')
            .replace('{postcode}', this.state.postcode || '')
        );
        this.updateStockNotice();
        return;
      }

      // No `_location` on NZ: the NZ buy box stopped writing it, and a
      // `_location` cart attribute would make its isValidLocation check
      // (product-form-pre-order.js) refuse later adds.

      this.setAdding(true);
      this.showCartError(null);

      var timestamp = Date.now();
      var reformerVariant = String(uph.variantId);

      try {
        var payload = this.buildCartPayload(timestamp, reformerVariant);
        var config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' }
        };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(payload);

        var url =
          (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') +
          '?sections=cart-drawer,cart-icon-bubble';

        var response = await fetch(url, config);
        var json = await response.json();

        if (json.status) {
          this.showCartError(json.description || json.message || 'We could not add that to your cart.');
          this.setAdding(false);
          return;
        }

        await fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              '_payment-plan': this.state.payment,
              '_delivery-postcode': this.state.postcode,
              '_fromB2B': null
            }
          })
        });

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'reformer-flow',
            productVariantId: reformerVariant,
            cartData: json
          });
        }

        this.announce(this.config.copy.addedAnnounce || 'Added to cart.');

        this.clearState();

        // The summary's Add to cart is the whole purchase, so it goes straight
        // to checkout — where the drawer's Checkout button posts to, with the
        // cart attributes above already saved. The button stays busy until the
        // page goes: a second tap here would add the build twice.
        if (options && options.checkout) {
          window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'checkout';
          return true;
        }

        // Add & Continue shopping with a link set on the block: same as above,
        // the button stays busy until the page goes.
        if (options && options.redirect) {
          window.location.href = options.redirect;
          return true;
        }

        // Quick add hands over to the cart drawer in place.
        var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.buildPanel.close('added');

        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(json);
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }
        this.setAdding(false);
        return true;
      } catch (error) {
        console.error('[reformer-flow] add to cart failed', error);
        this.showCartError(this.config.copy.addFailed || 'Something went wrong. Please try again.');
        this.setAdding(false);
      }
    }

    buildCartPayload(timestamp, reformerVariant) {
      var self = this;
      var items = [];

      items.push({
        id: reformerVariant,
        quantity: 1,
        properties: {
          '_bundle-product': reformerVariant,
          '_timeStamp': timestamp
        }
      });

      var prop = this.findPropKit(this.state.propKit);
      if (prop) {
        items.push({
          id: String(prop.variantId),
          quantity: 1,
          properties: {
            '_bundled-product': reformerVariant,
            '_timeStamp': timestamp
          }
        });
      }

      (this.config.inclusions || []).forEach(function (inc) {
        var resolved = self.resolveInclusion(inc);
        if (!resolved || !resolved.variantId) return;
        var props = { '_timeStamp': timestamp };
        var property = inc.property || '_bundle-included';
        // NZ buy box: on a Bond the jumpboard ships as the rental essentials
        // gift, not as a bundled variant (product-form-pre-order.js).
        if (inc.key === 'jumpboard' && self.state.payment === 'BOND') property = '_bundle-gift';
        props[property] = reformerVariant;
        items.push({ id: String(resolved.variantId), quantity: 1, properties: props });
      });

      // Inclusion products, as the old buy box added them from its inclusion
      // cards (product-form-pre-order.js): the timestamp always, and
      // _bundle-gift only when as_bundled_product is ticked — that property is
      // what groups the line under the Reformer and lets the bundle discount
      // zero it. Without it the line is charged like any other.
      (this.config.inclusionProducts || []).forEach(function (entry) {
        var option = self.resolveInclusionProduct(entry);
        if (!option) return;
        var props = { '_timeStamp': timestamp };
        if (entry.bundled) props['_bundle-gift'] = reformerVariant;
        items.push({ id: String(option.variantId), quantity: 1, properties: props });
      });

      // Paid extras are ordinary line items. They deliberately carry none of
      // the bundle properties above: those four mark an item as an inclusion,
      // and the store's bundle discount zeroes anything wearing one — which
      // would hand the customer a $149 platform extender for nothing. They
      // keep the timestamp so they can still be traced back to this build.
      Object.keys(this.state.extras).forEach(function (productId) {
        var variantId = self.state.extras[productId];
        if (!variantId) return;
        items.push({
          id: String(variantId),
          quantity: 1,
          properties: {
            '_timeStamp': timestamp
          }
        });
      });

      return {
        items: items.reverse(),
        attributes: {
          '_payment-plan': this.state.payment,
          '_delivery-postcode': this.state.postcode,
          '_fromB2B': null
        }
      };
    }

    /** The warehouse group name for this zone ("NSW/ACT"), as the locations
     *  service names it. resolveLocationId turns it into the id the cart needs. */
    locationValue() {
      var map = this.config.locationMap || {};
      return map[this.state.zone] || this.state.zone || '';
    }

    setAdding(busy) {
      if (!this.addBtn) return;
      this.addBtn.classList.toggle('is-loading', busy);
      var blocked = !!(this.stockProblems && this.stockProblems.length);
      this.addBtn.setAttribute('aria-disabled', busy || blocked ? 'true' : 'false');
      this.addBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    showCartError(message) {
      if (!this.cartError) return;
      this.cartError.hidden = !message;
      if (message) {
        this.cartError.textContent = message;
        this.announce(message);
      }
    }

    /** After a successful add the build is spent: the next "Build Your
     *  Reformer" starts a fresh one. The postcode survives — we already know
     *  where they are, and asking twice is the thing this flow exists to stop. */
    clearState() {
      this.state.propKit = null;
      this.state.propKitPicked = false;
      this.state.extras = {};
      this.state.inclusionPicks = {};
      this.state.stepIndex = 0;
      this.explicitExtraPicks = {};
      this.returnToSummary = false;

      this.extrasPickerProduct = null;
      this.extrasPickerOpener = null;

      this.querySelectorAll(
        '[data-rf-control="propKit"], [data-rf-control="extra"], [data-rf-control="inclusion"]'
      ).forEach(function (input) {
        input.checked = false;
      });
      this.querySelectorAll('[data-rf-extra-picker]').forEach(function (picker) {
        picker.hidden = true;
      });
      if (this.extrasPickerView) this.extrasPickerView.hidden = true;
      if (this.extrasList) this.extrasList.hidden = false;

      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch (e) {
        /* ignore */
      }

      // The old build's link must not outlive the build.
      this.syncUrl();
    }

    /* ---- live region ---- */

    announce(message) {
      if (!this.announcer || !message) return;
      // Re-setting identical text does not re-announce, so clear first.
      this.announcer.textContent = '';
      var el = this.announcer;
      window.setTimeout(function () {
        el.textContent = message;
      }, 60);
    }
  }

  if (!customElements.get('reformer-flow')) {
    customElements.define('reformer-flow', ReformerFlow);
  }
})();
